import { ImageName } from "@mcleod/images";
import { CommonDialogs, ImageTable, ModelUsers } from "@mcleod/common";
import {
    BlurEvent, Button, ChangeEvent, ClickEvent, ContextMenuCreationEvent, CrudDecoratorCloseEvent, DataDisplayEvent,
    DataHeaderCloseAction, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode,
    DataSourceModeChangeEvent, DataSourceValidationEvent, Dialog, DropdownItem, DropdownSelectionEvent, EditRowDecorator, Label, Layout,
    LayoutLoadEvent, LayoutProvidedCrudDecoratorProps, LookupModelSelectionEvent, ModelDataChangeType, Panel,
    SaveButton, SlideoutDecorator, Snackbar, Table, TableAction, TableContentsChangedEvent, TableRow,
    TableRowBeforeSaveEvent, Tabset, Textbox, Toast, ValidationResult
} from "@mcleod/components";
import {
    Alignment, Api, ArrayUtil, CurrencySettings, DisplayType, FieldUpdateEvent, HorizontalAlignment, LogManager,
    McLeodClassicIntegration, Model, ModelRow, ModelRowType, Navigation, StringUtil, VerticalAlignment
} from "@mcleod/core";
import { ModelEdiorderProfile, OrderLoadTenderHistory, RowEdiorderProfile } from "@mcleod/datafusion";
import { ModelEdiCustomer } from "@mcleod/datafusion/src/models/ModelEdiCustomer";
import {
    AssetUtil, LMECompanySettings, LMEGeneralSettings, LMEUserSettings, UsersResponsibilitySlideoutTrans
} from "@mcleod/general";
import { CarrierQuickInfo } from "@mcleod/powerbroker";
import { AutogenLayoutEdiBilling } from "./autogen/AutogenLayoutEdiBilling";
import {
    AppointmentUtil, BrltlUtil, DuplicateBOLSource, EquipmentTypeQuickInfo,
    OrderHandlingRequirementsSlideout, OrderPostHist, OrderPostHistProps, OrderStop, OrderTabView,
    RearrangeStopSlideout, ShipmentStatusSlideout
} from "@mcleod/dispatch";
import { ViewBilling } from "@mcleod/ar/src/ViewBilling";
import { EdiBillingOverview } from "./EdiBillingOverview";
import { StopProgress } from "@mcleod/dispatch/src/StopProgress";
import { ManageStops } from "@mcleod/dispatch/src/ManageStops";
import {
    setBatchCodeTooltip, setPartnerNameQuickInfo, generateTransmissionLogQuickInfo, EdiUtility
} from "./EdiUtility";
import { EdiBillingUtility } from "./EdiBillingUtility";
import * as BillingUtil from "@mcleod/ar/src/BillingUtil";
import { CompareHistoryList } from "./CompareHistoryList";
import { EdiRawDataSlideout } from "./EdiRawDataSlideout";
import { reloadEdiBilling } from "./EdiBillingUtil";
import { InvoiceCustomerCrossReference } from "./InvoiceCustomerCrossReference";
import { CompareInvoice } from "./CompareInvoice";
import { EdiStop } from "./EdiStop";
import { RowPostListener } from "@mcleod/core/src/ModelRow";

const log = LogManager.getLogger("lme.datafusion.EdiBilling");
const ALL_ITEM = "ALL"
const CREDIT_TYPE = "C";

const COMPARED = "C";
const REJECTED = "R";
const PENDING = "P";
const TRANSFERRED = "T";
const DISREGARDED = "D";
const OUTBOUND = "O";
const INBOUND = "I";

export { isSpotOrder } from "@mcleod/ar/src/BillingUtil";

/**
 * EDI Billing Layout
 * Handles EDI billing details using the edi-billing-detail model
 */
export class EdiBilling extends AutogenLayoutEdiBilling {

    private _doAfterAction: () => void = () => this.mainDataSource.search({ id: this.mainDataSource.activeRow.get("id") });
    private _invokedFromEdi: boolean = false;
    private _currencyFromProfile: string = null;
    private loadingRecurringOrder: boolean = false;
    private _isBrokerageLtlCompany: boolean = false;
    private _isAsset: boolean = false;
    private shouldOpenPaySlideout: boolean;
    private _holdReasonChanged: boolean = false;
    private beforePostListener: RowPostListener;

    private static orderCurrencies: string[] = ["linehaul_chg", "other_charge", "total_charges",
        "excisetax_total", "totalcharge_and_excisetax", "est_tolls", "pay_gross"];
    public carrierAssignMentChanged: () => void;

    override onLoad(): void {
        this.tabsetUpdateBilling["_header"].borderTopWidth = 0;
        this.addOnSelectListeners();
        this.addBeforeLookupModelListeners();

        this.sourceEdiBillingDetails.addValidationListener(this.validateStopsBeforeSave.bind(this));

        this.stopsTable.onEditLayoutLoaded = this.stopTableOnEditLoaded();
        this.ediStopsTable.onEditLayoutLoaded = this.ediStopTableOnEditLoaded();

        this.labelHoldBadge.tooltipDynamicContent = () => {
            return this.mainDataSource.activeRow?.get("hold_reason");
        }
        this.dataHeader["labelTitle"].minWidth = 220;

        this.layoutEdiBillingRates.checkPickupAndDeliveryEntered = () => {
            const stopData = this.sourceStop.data;
            if (stopData.length < 2) return false;
            const pickupEntered = stopData[0].get("stop_type") === "PU" && stopData[0].get("city_id") != null;
            const deliveryEntered = stopData[stopData.length - 1].get("stop_type") === "SO" && stopData[stopData.length - 1].get("city_id") != null;
            return pickupEntered && deliveryEntered;
        }
        this.addAdditionalMainDataSourceListeners();
        this.layoutEdiBillingRates.layoutMovementRates.panelOrderCharges.visible = false;
        this.stopsTable.rowsAreDraggable = () => false;
        this.ediStopsTable.rowsAreDraggable = () => false;
        this.textboxOrderLength.visible = LMEGeneralSettings.getSingleton().getDispatchControl()["enable_manifesting"] === "Y";
        this.layoutShipperPONums.textboxShipperPONums.enabled = false;
        EdiBillingUtility.styleTab(this.tabErrors);

        // Set up lookup model selection listeners for EDI field tooltips
        this.txtCorrectionIndicator.addLookupModelSelectionListener(() => {
            const ediRow = this.sourceEdiBillingDetails.activeRow;
            if (ediRow) {
                EdiBillingUtility.makeTooltipContent(this.txtCorrectionIndicator, ediRow);
            }
        });

        this.txtServiceCode.addLookupModelSelectionListener(() => {
            const ediRow = this.sourceEdiBillingDetails.activeRow;
            if (ediRow) {
                EdiBillingUtility.makeTooltipContent(this.txtServiceCode, ediRow);
            }
        });

        // Set up click listeners for EDI billing overview error icons
        this.layoutOverview.imageInboundDFInvoiceError.addClickListener(event => {
            this.tabsetUpdateBilling.scrollToTab(this.tabDataFusion);
            this.tabsetErrors.expandAll();
        });
        // Set up click listeners for EDI billing overview error icons
        this.layoutOverview.imageInboundWarning.addClickListener(event => {
            this.tabsetUpdateBilling.scrollToTab(this.tabDataFusion);
            this.tabsetErrors.expandAll();
        });
        this.layoutOverview.imageOutboundDFInvoiceError.addClickListener(event => {
            this.tabsetUpdateBilling.scrollToTab(this.tabDataFusion);
            this.tabsetErrors.expandAll();
        });
        this.layoutOverview.imageOutboundDFInvoiceWarning.addClickListener(event => {
            this.tabsetUpdateBilling.scrollToTab(this.tabDataFusion);
            this.tabsetErrors.expandAll();
        });

        this.setupEquipmentTypeIdTooltipOverrides(this.textboxEquipmentTypeIdGeneral);
        this.setupEquipmentTypeIdTooltipOverrides(this.textboxEquipmentTypeIdEquipment);
    }

    filterAvailableMoves(data: any) {
        return BillingUtil.filterAvailableMoves(data);
    }

    addMovementPickupTimes(tab: Panel, movement: ModelRow<any>) {
        BillingUtil.addMovementPickupTimes(tab, movement);
    }

    addSplitImageToTab(tab: Panel, movement: ModelRow) {
        BillingUtil.addSplitImageToTab(tab, movement);
    }


    textboxBlnumOnBlur(event: BlurEvent) {
        if (!this.mainDataSource.activeRow?.isNull("blnum") && !this.switchRecurringOrder.checked) {
            let source: DuplicateBOLSource;
            if (this.mainDataSource.mode == DataSourceMode.ADD) {
                source = DuplicateBOLSource.ADD;
            } else if (this.mainDataSource.mode == DataSourceMode.UPDATE && this.activeRow?.getChangedData()["blnum"] != null) {
                source = DuplicateBOLSource.UPDATE;
            }
        }
    }

    async getPendingBadgeTooltipData(modelRow: ModelRow) {
        let tooltipData = null;
        if (modelRow?.isNull("pending_payee_id")) {
            const pendingCarrierType = modelRow.get("pending_carrier_type");
            const pendingCarrierNumber = modelRow.get("pending_carrier_nbr");
            tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
                movement_id: modelRow.get("id"),
                locked_user: modelRow.get("locked_user_id"),
                name: modelRow.get("pending_carrier_name"),
                pending_carrier: true,
                ...(pendingCarrierType == "M" ? { icc_number: pendingCarrierNumber } : {}),
                ...(pendingCarrierType == "D" ? { dot_number: pendingCarrierNumber } : {}),
                ...(pendingCarrierType == "I" ? { intrastate_code: pendingCarrierNumber } : {})
            });
        } else {
            const result = await Api.search("lme/powerbroker/carriers-for-assignment", { id: modelRow?.get("pending_payee_id") });
            const payeeData = result?.data[0];
            tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
                movement_id: modelRow?.get("id"),
                locked_user: modelRow?.get("locked_user_id"),
                name: payeeData.name,
                phone_number: payeeData.phone_number ?? "",
                icc_number: payeeData.icc_number,
                dot_number: payeeData.dot_number,
                intrastate_code: payeeData.intrastate_code,
                pending_carrier: false,
                id: payeeData.id
            });
        }
        return tooltipData;
    }

    async setTooltipPendingLabelCallback(pendingLabel: Label, modelRow: ModelRow) {
        pendingLabel.tooltipDynamicContent = async () => {
            const pendingTooltipData = await this.getPendingBadgeTooltipData(modelRow);
            if (pendingTooltipData) {
                return this.getPendingBadgeLayout(pendingTooltipData);
            }
        };
    }

    getPendingBadgeLayout(modelRow: ModelRow): Layout {
        const layout = Layout.getLayout("lme/powerbroker/CarrierQuickInfo") as CarrierQuickInfo;
        layout.addLayoutLoadListener((event: LayoutLoadEvent) => {
            event.layout.mainDataSource.data = [modelRow];
            layout.mainDataSource.rowIndex = 0;
        });
        layout.pendingCancelled = (lock_changed, user_can_unlock) => {
            if (lock_changed && user_can_unlock) {
                this.sourceOrders.search({ id: this.sourceOrders.activeRow?.get("id") });
            }
        }
        return layout;
    }

    addPendingLabelToTab(tab: Panel, movement: ModelRow) {
        BillingUtil.addPendingLabelToTab(tab, movement, this.setTooltipPendingLabelCallback.bind(this));
    }

    createPendingLabel() {
        return BillingUtil.createPendingLabel();
    }

    createMovementTimeLabel(movement: ModelRow<any>) {
        return BillingUtil.createMovementTimeLabel(movement);
    }

    createPostedMovementTimeLabel(movement: ModelRow<any>) {
        return BillingUtil.createPostedMovementTimeLabel(movement);
    }
    updatePnnRateField(movement: ModelRow<any>, restoreExistingRate: boolean) {
        let pnnRate = movement.get("override_targetpay", movement.get("target_pay", null));
        if (restoreExistingRate) {
            if (!StringUtil.isEmptyString(this.sourceOrders.activeRow?.get("pnn_rate"))) {
                pnnRate = this.sourceOrders?.activeRow?.get("pnn_rate");
            }
        }
    }

    textboxPnnLoadboardUserIdOnChange(event) {
        const orderRow = this.sourceOrders.activeRow;
        if (orderRow != null) {
            this.setUpdatedUserPnnCallbackNumber(orderRow.get("pnn_loadboard_user_id"));
        }
    }
    private async setUpdatedUserPnnCallbackNumber(userId: string) {
        if (userId != null) {
            await new ModelUsers().searchSingle({ id: userId }, "common/UserLookup").then(userRow => {
                if (!userRow) return;
                if (userRow.get("phone")) {
                    this.sourceOrders.activeRow.set("pnn_callback", userRow.get("phone"));
                } else {
                    this.sourceOrders.activeRow.set("pnn_callback", "");
                }
            })
        }
    }


    private _setPreferredVendorLookupModelData(row: ModelRow) {
        const lookupData = row?.get("preferred_vendors_lookup_data");
        if (lookupData != null) {
            const newLookupData = lookupData.map(data => {
                const row = new ModelRow("lme/powerbroker/pbw-loadboard-services", false, { ...data });
                row.type = ModelRowType.LOOKUP_MODEL_DATA;
                return row;
            });
            row.setLookupModelData("preferred_vendors", newLookupData);
            row.set("preferred_vendors_lookup_data", undefined);
        }
    }

    private async setCommodity(apiData?: any) {
        const commodityId = this.sourceEdiBillingDetails.activeRow?.get("commodity");
        if (commodityId === null || commodityId === undefined) {
            return;
        }
        const response = await Model.searchSingleRecord("lme/dispatch/commodity", { id: commodityId });

        if (response != null) {
            const lmModelRow = new ModelRow(this.textboxCommodityId.lookupModel, false, {
                id: response.get("id"),
                descr: apiData?.commodity ?? response.get("descr"),
                temperature_min: apiData?.temperature_min ?? response.get("temperature_min"),
                temperature_max: apiData?.temperature_max ?? response.get("temperature_max"),
                is_hazmat: response.get("is_hazmat"),
                hazmat_code: response.get("hazmat_number"),
                hazmat_number: response.get("hazmat_number"),
                hazmat: response.get("hazmat")
            });

            lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;

            // Set the lookup data and field value on the EDI billing details data source
            this.sourceEdiBillingDetails.activeRow.setLookupModelData("commodity", lmModelRow);
            this.sourceEdiBillingDetails.activeRow.set("commodity", response.get("id"));

            // Set hazmat code data on the EDI billing details data source as well
            this.sourceEdiBillingDetails.activeRow.setLookupModelData("hazmat_code", lmModelRow);
            this.sourceEdiBillingDetails.activeRow.set("hazmat_code", response.get("hazmat_number"));
        }
    }

    private addAdditionalMainDataSourceListeners() {
        this.mainDataSource.componentValidationFailedCallback = (result: ValidationResult) => this.onComponentValidationFailed(result);
        this.mainDataSource.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => this.sourceEdiBillingDetailsAfterGetDataboundValues(row, dataSource);

        // Add afterExecution listener to handle commodity display after data is loaded
        this.sourceEdiBillingDetails.addAfterExecutionListener(async (event) => {
            console.log("EDI Billing Details afterExecution triggered");
            // Check if we have commodity data and set it up for display
            if (this.sourceEdiBillingDetails.activeRow?.get("commodity")) {
                console.log("Found commodity in data, calling setCommodity");
                await this.setCommodity();
            }

            const isBalanceDue = this.sourceEdiBillingDetails.activeRow?.get("is_balance_due") === "Y";
            this.textboxInvoiceNoString.enabled = isBalanceDue;
        });
    }
    private addOnSelectListeners() {

        this.textboxRevenueCodeId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                if (this.textboxRevenueCodeId.getFirstLookupModelData()?.get("id") != null)
                    this.revenueCodeChanged(this.textboxRevenueCodeId.getFirstLookupModelData().get("id"));
            }
        });

        this.textboxOrderType.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                if (this.textboxOrderType.getFirstLookupModelData()?.get("id") != null)
                    this.orderTypeChanged(this.textboxOrderType.getFirstLookupModelData().get("id"));
            }
        });

        this.textboxCustomerId.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                if (this.textboxCustomerId.getFirstLookupModelData()?.get("id") != null)
                    (this.layoutOverview as unknown as EdiBillingOverview).customerChanged(this.textboxCustomerId.getFirstLookupModelData());

                if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                    this.layoutEdiBillingRates.convertPricingCurrencies(event.selection.get("currency_type", await this.getTransactionalCurrency()));
                    if ((this.sourceOrders.mode == DataSourceMode.UPDATE)
                        && !this.sourceOrders.activeRow.isNull("customer_id")) {
                        this.layoutEdiBillingRates.panelExciseTaxTotal.visible = event.selection.getBoolean("excise_taxable", false);
                    }
                }
            }
        });
        this.textboxDefMoveType.addChangeListener((event: ChangeEvent) => {
            this.isAsset = event.newValue === "Asset";
        });
    }

    private addBeforeLookupModelListeners() {
        const userfilter = event => event.filter.web_user_type = "U";
        this.textboxBillingUserId.addBeforeLookupModelSearchListener(userfilter);
        this.textboxEnteredUserId.addBeforeLookupModelSearchListener(userfilter);
    }

    public get isAsset(): boolean {
        return this._isAsset;
    }

    public set isAsset(value: boolean) {
        this._isAsset = value;
        this.textboxBrkQualProfile.visible = !value;
        this.textboxBrokerageWorkflowId.visible = !value;
        this.layoutEdiBillingRates.carrierPayLayout.visible = !AssetUtil.isAssetMoveType(value)
            && (this.activeRow == null || !this.activeRow?.getBoolean("multi_movement", false))
        if (value && this.tableEquipmentRequirementsBrokerage.visible) {
            this.tableEquipmentRequirementsBrokerage.visible = false;
            this.tableEquipmentRequirementsAsset.visible = true;
            this.tableEquipmentRequirementsAsset.dataSource.displayDataInBoundComponents();
        } else if (!value && this.tableEquipmentRequirementsAsset.visible) {
            this.tableEquipmentRequirementsAsset.visible = false;
            this.tableEquipmentRequirementsBrokerage.visible = true;
            this.tableEquipmentRequirementsBrokerage.dataSource.displayDataInBoundComponents();
        }
    }

    public get isBrokerageLtlCompany(): boolean {
        return this._isBrokerageLtlCompany;
    }

    public set isBrokerageLtlCompany(value: boolean) {
        const direction = this.mainDataSource?.activeRow?.get("direction");
        const isInbound = direction === "I";
        this._isBrokerageLtlCompany = value;
        this.tabBrltlFreightItems.visible = value;
        this.tabAccessorials.visible = value;
        this.layoutEdiBillingRates.panelRatingDetails.visible = value;
        if (value && this.sourceOrders.mode === DataSourceMode.ADD) {
            this.addBlankItemIfBRLTL();
            this.textboxRevenueCodeId.addBlurListener(() => {
                this.addBlankItemIfBRLTL();
            });
            this.textboxOrderType.addBlurListener(() => {
                this.addBlankItemIfBRLTL();
            });
        }

        if (value) {
            this.layoutFreightItems.doBeforeCommoditySearch = (filter: any) => {
                filter.product_book_location = this.sourceStop?.data?.[0].get("location_id");
                filter.product_book_customer = this.activeRow?.get("customer_id");
            };
            this.textboxBolNote.visible = true;
        }

        this.textboxControllingCarrierPro.visible = this.isBrokerageLtlCompany;
        this.textboxFrtGrpProNbr.visible = this.isBrokerageLtlCompany;
    }

    public override get crudDecoratorProps(): Partial<LayoutProvidedCrudDecoratorProps> {
        return { customDataLoader: this.loadFromApiData.bind(this) };
    }

    public loadFromApiData = (apiData: any, mode: DataSourceMode) => {
        const billingDetails = new ModelRow("lme/datafusion/edi-billing-detail", true, apiData);
        this.sourceEdiBillingDetails.setRowsAndMode(mode, [billingDetails]);

        // Initialize the layout after data is loaded
        setTimeout(async () => {
            this.initialize();

            // Populate customer lookup data if customer_id is present
            if (apiData.customer_id) {
                await this.populateCustomerLookupData(apiData.customer_id);
            }

            // Populate revenue code lookup data if revenue_id is present
            if (apiData.revenue_id) {
                await this.populateRevenueCodeLookupData(apiData.revenue_id);
            }

            // Populate order type lookup data if order_type_id is present
            if (apiData.order_type_id) {
                await this.populateOrderTypeLookupData(apiData.order_type_id);
            }

            // Note: Commodity data will be populated automatically via the afterExecution listener
            // in addAdditionalMainDataSourceListeners() when the data source loads

            // Populate EDI billing profile data if partner information is present
            if (apiData.partner_id && apiData.version) {
                await this.populateEdiBillingProfileData(apiData.partner_id, apiData.version, apiData.alt_partner_id);
            }

            // Populate stops and orders data if order_id is present (for balance due transactions)
            // This ensures that the Pickups and Deliveries tab shows the stops from the original order
            // and that order-related fields in the General and Equipment tabs are properly populated
            if (apiData.order_id) {
                await this.populateStopsData(apiData.order_id);
                await this.populateOrdersData(apiData.order_id);

                // Call afterOrdersExecution to set up tooltips and additional processing
                await this.afterOrdersExecution();

                // Populate currency data AFTER orders data is loaded and bound
                await this.populateCurrencyData(apiData);
            } else {
                // If no order_id, still populate currency data
                await this.populateCurrencyData(apiData);
            }

            // Populate OTHER_CHARGES data if present in the API response
            if (apiData.other_charges && Array.isArray(apiData.other_charges)) {
                await this.populateOtherChargesData(apiData.other_charges);
            }

            if (CurrencySettings.getSingleton().multiCurrencyEnabled() && apiData.tax_charges && Array.isArray(apiData.tax_charges)) {
                await this.populateTaxChargesData(apiData.tax_charges);
            }

            // Populate commodity data after all other data is loaded
            // This needs to be called manually here because setRowsAndMode doesn't trigger afterExecution
            if (apiData.commodity || this.sourceEdiBillingDetails.activeRow?.get("commodity")) {
                console.log("Calling setCommodity from loadFromApiData");
                await this.setCommodity(apiData);
            }

            // Hide components not needed for balance due transactions
            if (mode === DataSourceMode.ADD) {
                this.hideComponentsForBalanceDue();
            }

            const orderStatus = this.sourceOrders?.activeRow?.get("status") || apiData.status;
            if (orderStatus) {
                const statusDisplayMap = { "A": "Available", "P": "Progress", "V": "Void", "D": "Delivered", "Q": "Quote" };
                this.labelStatusBadge.text = statusDisplayMap[orderStatus] || orderStatus;
            }
        }, 0);
    }

    private initialize() {
        // Don't initialize if there's no active row (e.g., after deletion)
        if (this.sourceEdiBillingDetails.activeRow == null) {
            return;
        }

        // Trigger any additional initialization logic needed for EDI billing
        // This could include setting up lookups, updating UI state, etc.
        if (this.sourceEdiBillingDetails.activeRow) {
            // Sync the data header title
            this.dataHeader?.syncTitle();
        }
    }

    private async populateCustomerLookupData(customerId: string) {
        try {
            // Search for customer data using the customer lookup model
            const customerData = await Model.searchSingleRecord("lme/ar/customer", { id: customerId });

            if (customerData && this.sourceEdiBillingDetails.activeRow) {
                // Create lookup model data for the customer
                const lookupModelRow = new ModelRow(this.textboxCustomerId.lookupModel, false, {
                    id: customerData.get("id"),
                    name: customerData.get("name"),
                    // Add any other fields that might be needed for display
                });
                lookupModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;

                // Set the lookup model data on the active row
                this.sourceEdiBillingDetails.activeRow.setLookupModelData("customer_id", lookupModelRow);

                // Force the textbox to display the data
                this.textboxCustomerId.displayData(this.sourceEdiBillingDetails.activeRow, null, 0);

                console.log("Successfully populated customer lookup data for customer ID:", customerId);
            }
        } catch (error) {
            console.warn("Could not populate customer lookup data:", error);
        }
    }

    private async populateRevenueCodeLookupData(revenueId: string) {
        try {
            // Search for revenue code data
            const revenueData = await Model.searchSingleRecord("lme/ar/revenue-code", { id: revenueId });

            if (revenueData && this.sourceEdiBillingDetails.activeRow) {
                // Create lookup model data for the revenue code
                const lookupModelRow = new ModelRow(this.textboxRevenueCodeId.lookupModel || "lme/ar/revenue-code", false, {
                    id: revenueData.get("id"),
                    descr: revenueData.get("descr"),
                    // Add any other fields that might be needed for display
                });
                lookupModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;

                // Set the lookup model data on the active row
                this.sourceEdiBillingDetails.activeRow.setLookupModelData("revenue_id", lookupModelRow);

                // Force the textbox to display the data
                this.textboxRevenueCodeId.displayData(this.sourceEdiBillingDetails.activeRow, null, 0);

                console.log("Successfully populated revenue code lookup data for revenue ID:", revenueId);
            }
        } catch (error) {
            console.warn("Could not populate revenue code lookup data:", error);
        }
    }

    private async populateOrderTypeLookupData(orderTypeId: string) {
        try {
            // Search for order type data
            const orderTypeData = await Model.searchSingleRecord("lme/dispatch/order-type", { id: orderTypeId });

            if (orderTypeData && this.sourceEdiBillingDetails.activeRow) {
                // Create lookup model data for the order type
                const lookupModelRow = new ModelRow(this.textboxOrderType.lookupModel || "lme/dispatch/order-type", false, {
                    id: orderTypeData.get("id"),
                    descr: orderTypeData.get("descr"),
                    // Add any other fields that might be needed for display
                });
                lookupModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;

                // Set the lookup model data on the active row
                this.sourceEdiBillingDetails.activeRow.setLookupModelData("order_type_id", lookupModelRow);

                // Force the textbox to display the data
                this.textboxOrderType.displayData(this.sourceEdiBillingDetails.activeRow, null, 0);

                console.log("Successfully populated order type lookup data for order type ID:", orderTypeId);
            }
        } catch (error) {
            console.warn("Could not populate order type lookup data:", error);
        }
    }

    private async populateStopsData(orderId: string) {
        try {
            // Search for stops data using the order_id
            const stopsResponse = await Api.search("lme/dispatch/stop", { order_id: orderId });

            if (stopsResponse?.data && stopsResponse.data.length > 0 && this.sourceStop) {
                // Create ModelRow instances for each stop
                // For balance due transactions, we want to display the stops but not save/post them
                // as they already exist in the database for the original order
                const stopRows = stopsResponse.data.map(stopData => {
                    // Create without trackChanges to prevent posting
                    const stopRow = new ModelRow("lme/dispatch/stop", false, stopData);
                    return stopRow;
                });

                // Set the stops data in UPDATE mode for component binding but rows won't post changes
                this.sourceStop.setRowsAndMode(DataSourceMode.UPDATE, stopRows);

                console.log(`Successfully populated ${stopRows.length} stops for order ID (read-only):`, orderId);
            }
        } catch (error) {
            console.warn("Could not populate stops data:", error);
        }
    }

    private async populateOrdersData(orderId: string) {
        try {
            // Search for orders data using the order_id
            const ordersResponse = await Api.search("lme/dispatch/orders", { id: orderId });

            if (ordersResponse?.data && ordersResponse.data.length > 0 && this.sourceOrders) {
                // Create ModelRow instances for each order (typically just one)
                // For balance due transactions, we need the order data for display and currency handling
                // but we don't want to post changes to the original order
                const orderRows = ordersResponse.data.map(orderData => {
                    // Create without trackChanges to prevent posting
                    const orderRow = new ModelRow("lme/dispatch/orders", false, orderData);
                    return orderRow;
                });

                // Set the orders data in UPDATE mode for component binding but rows won't post changes
                this.sourceOrders.setRowsAndMode(DataSourceMode.UPDATE, orderRows);

                console.log(`Successfully populated ${orderRows.length} order(s) for order ID (read-only):`, orderId);
            }
        } catch (error) {
            console.warn("Could not populate orders data:", error);
        }
    }

    private async populateCurrencyData(apiData?: any) {
        try {
            let currency: string;

            // First try to use the currency from the API data if available
            if (apiData && apiData.linehaul_chg_c) {
                currency = apiData.linehaul_chg_c;
                console.log("Using currency from API data:", currency);
            } else {
                // Fallback to getting the transactional currency
                currency = await this.layoutEdiBillingRates.getTransactionalCurrency();
                console.log("Using transactional currency as fallback:", currency);
            }
            this.layoutEdiBillingRates.setOtherChargesUtilCurrency(currency);

            if (currency) {

                // Set the currency field (linehaul_chg_c) in the orders data source since that's what the textbox is bound to
                if (this.sourceOrders.activeRow) {
                    this.sourceOrders.activeRow.set("linehaul_chg_c", currency);

                    // Create lookup model data for the currency textbox
                    const currencyLookupData = new ModelRow("lme/general/multicurrency/currency-available", false, {
                        currency_type_id: currency,
                        // Add any other fields that might be needed for display
                    });
                    currencyLookupData.type = ModelRowType.LOOKUP_MODEL_DATA;

                    // Set the lookup model data on the orders row
                    this.sourceOrders.activeRow.setLookupModelData("linehaul_chg_c", currencyLookupData);

                    // Add a small delay to ensure component binding is complete, then force refresh
                    setTimeout(() => {
                        if (this.layoutEdiBillingRates && this.layoutEdiBillingRates.textboxCurrencyType) {
                            // Ensure the component is visible
                            this.layoutEdiBillingRates.textboxCurrencyType.visible = true;

                            // Force the textbox to refresh its display
                            this.layoutEdiBillingRates.textboxCurrencyType.displayData(this.sourceOrders.activeRow, null, 0);

                            // Also try setting the text directly as a fallback
                            this.layoutEdiBillingRates.textboxCurrencyType.text = currency;

                            console.log("Currency textbox refreshed with value:", currency);
                        }
                    }, 100);
                }

                console.log("Successfully populated currency data:", currency);
            } else {
                console.warn("No currency value found in API data, billing details, or transactional currency");
            }
        } catch (error) {
            console.warn("Could not populate currency data:", error);
        }
    }

    private async populateEdiBillingProfileData(partnerId: string, version: string, altPartnerId?: string) {
        try {
            // Search for EDI billing profile data using the partner information
            const searchParams: any = {
                partner_id: partnerId,
                version: version
            };

            // Add alt_partner_id if provided
            if (altPartnerId) {
                searchParams.alt_partner_id = altPartnerId;
            }

            const profileData = await Model.searchSingleRecord("lme/datafusion/edibilling-profile", searchParams);

            if (profileData && this.sourceEdiBillingDetails.activeRow) {
                // Set the profile data on the billing details row
                // The textboxPartnerName field is bound to "edibilling_profile.partner_name"
                // so we need to set the profile data in a way that this field will be populated
                this.sourceEdiBillingDetails.activeRow.set("edibilling_profile.partner_name", profileData.get("partner_name"));
                this.sourceEdiBillingDetails.activeRow.set("edibilling_profile.partner_id", profileData.get("partner_id"));
                this.sourceEdiBillingDetails.activeRow.set("edibilling_profile.version", profileData.get("version"));

                if (altPartnerId) {
                    this.sourceEdiBillingDetails.activeRow.set("edibilling_profile.alt_partner_id", profileData.get("alt_partner_id"));
                }

                // Force the textbox to display the data
                if (this.textboxPartnerName) {
                    this.textboxPartnerName.displayData(this.sourceEdiBillingDetails.activeRow, null, 0);
                }

                console.log("Successfully populated EDI billing profile data for partner:", partnerId, "version:", version);
            }
        } catch (error) {
            console.warn("Could not populate EDI billing profile data:", error);
        }
    }

    private async populateOtherChargesData(otherChargesData: any[]) {
        try {
            const ediBillingRates = this.layoutEdiBillingRates;
            if (ediBillingRates && ediBillingRates.sourceOtherChargeEdi && ediBillingRates.tableOtherCharges) {
                // Clear existing data
                ediBillingRates.sourceOtherChargeEdi.data = [];

                // Create ModelRow objects from the OTHER_CHARGES data
                for (const chargeData of otherChargesData) {
                    const modelRow = new ModelRow(ediBillingRates.sourceOtherChargeEdi.url, false);
                    // Set the basic charge data fields
                    Object.keys(chargeData).forEach(key => {
                        modelRow.set(key, chargeData[key]);
                    });

                    // If charge_id is present, populate its lookup model data
                    if (chargeData.charge_id) {
                        try {
                            const chargeCode = await Model.searchSingleRecord("lme/dispatch/charge-code", { id: chargeData.charge_id });
                            if (chargeCode) {
                                const lookupModelRow = new ModelRow("lme/dispatch/charge-code", false, {
                                    id: chargeCode.get("id"),
                                    descr: chargeCode.get("descr"),
                                    // Add any other fields that might be needed for display
                                });
                                lookupModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                                modelRow.setLookupModelData("charge_id", lookupModelRow);
                            }
                        } catch (error) {
                            console.warn("Could not populate charge code lookup data for charge_id:", chargeData.charge_id, error);
                        }
                    }

                    ediBillingRates.sourceOtherChargeEdi.data.push(modelRow);
                }

                // Refresh the table display with the new data
                ediBillingRates.tableOtherCharges.displayData(null, ediBillingRates.sourceOtherChargeEdi.data, 0);
                console.log("Successfully populated other charges table with", otherChargesData.length, "items");
            } else {
                console.warn("EdiBillingRates components not available for populating other charges");
            }
        } catch (error) {
            console.error("Error populating other charges data:", error);
        }
    }

    private async populateTaxChargesData(taxChargesData: any[]) {
        try {
            const ediBillingRates = this.layoutEdiBillingRates;
            if (ediBillingRates) {
                ediBillingRates.panelExciseTaxTotal.visible = true;
                ediBillingRates.panelTotalWithTaxes.visible = true;
            }
            if (ediBillingRates && ediBillingRates.sourceExcisetaxChargeEdi && ediBillingRates.tableTax) {
                // Clear existing data
                ediBillingRates.tableTax.allowEdit = true;
                ediBillingRates.isBalanceDue = true;

                ediBillingRates.sourceExcisetaxChargeEdi.data = [];

                // Create ModelRow objects from the OTHER_CHARGES data
                for (const chargeData of taxChargesData) {
                    const modelRow = new ModelRow(ediBillingRates.sourceExcisetaxChargeEdi.url, false);

                    // Set the basic charge data fields
                    Object.keys(chargeData).forEach(key => {
                        modelRow.set(key, chargeData[key]);
                    });

                    if (chargeData.state_id) {
                        try {
                            const lookupModelRow = new ModelRow("common/states", false, {
                                id: chargeData.state_id
                                // Add any other fields that might be needed for display
                            });
                            lookupModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                            modelRow.setLookupModelData("state_id", lookupModelRow);
                        } catch (error) {
                            console.warn("Could not populate state code lookup data for state_id:", chargeData.state_id, error);
                        }
                    }

                    if (chargeData.excisetax_id) {
                        try {
                            const chargeCode = await Model.searchSingleRecord("lme/ar/excisetax-code", { id: chargeData.excisetax_id });
                            if (chargeCode) {
                                const lookupModelRow = new ModelRow("lme/ar/excisetax-code", false, {
                                    id: chargeCode.get("id"),
                                    descr: chargeCode.get("descr"),
                                    // Add any other fields that might be needed for display
                                });
                                lookupModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                                modelRow.setLookupModelData("excisetax_id", lookupModelRow);
                            }
                        } catch (error) {
                            console.warn("Could not populate excise tax id lookup data for excisetax_id:", chargeData.excisetax_id, error);
                        }
                    }

                    if (chargeData.customer_id) {
                        try {
                            const chargeCode = await Model.searchSingleRecord("lme/ar/customer-lookup", { id: chargeData.customer_id });
                            if (chargeCode) {
                                const lookupModelRow = new ModelRow("lme/ar/customer-lookup", false, {
                                    id: chargeCode.get("id"),
                                    name: chargeCode.get("name"),
                                    // Add any other fields that might be needed for display
                                });
                                lookupModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                                modelRow.setLookupModelData("customer_id", lookupModelRow);
                            }
                        } catch (error) {
                            console.warn("Could not populate customer lookup data for customer_id:", chargeData.customer_id, error);
                        }
                    }

                    ediBillingRates.sourceExcisetaxChargeEdi.data.push(modelRow);
                }

                // Refresh the table display with the new data
                ediBillingRates.tableTax.displayData(null, ediBillingRates.sourceExcisetaxChargeEdi.data, 0);
                console.log("Successfully populated tax charges table with", taxChargesData.length, "items");
            } else {
                console.warn("EdiBillingRates components not available for populating tax charges");
            }
        } catch (error) {
            console.error("Error populating tax charges data:", error);
        }
    }

    private hideComponentsForBalanceDue() {
        try {
            // Hide tabs not needed for balance due transactions
            if (this.tabFunctionalAck) this.tabFunctionalAck.visible = false;
            if (this.tabAccessorials) this.tabAccessorials.visible = false; // Additional charges/accessorials

            // Hide the Movement View tab panel
            if (this.panelMovementTab) this.panelMovementTab.visible = false;

            // Hide components bound to orders table that are not essential for billing
            if (this.textboxBolRecvDate) this.textboxBolRecvDate.visible = false; // orders.bol_recv_date
            if (this.textboxRecurringOrderId) this.textboxRecurringOrderId.visible = false; // orders.recurring_order_id
            if (this.textboxOrderOrderedBy) this.textboxOrderOrderedBy.visible = false; // orders.ordered_by
            if (this.textboxOrderMode) this.textboxOrderMode.visible = false; // orders.order_mode
            if (this.textboxRateSource) this.textboxRateSource.visible = false; // orders.rate_source
            if (this.textboxDefMoveType) this.textboxDefMoveType.visible = false; // orders.def_move_type
            if (this.textboxPlanningComment) this.textboxPlanningComment.visible = false; // orders.planning_comment
            if (this.textboxBolNote) this.textboxBolNote.visible = false; // orders.bol_note
            if (this.textboxBrkQualProfile) this.textboxBrkQualProfile.visible = false; // orders.brk_qual_profile
            if (this.textboxBrokerageWorkflowId) this.textboxBrokerageWorkflowId.visible = false; // orders.brokerage_workflow_id
            if (this.switchHoldLoad) this.switchHoldLoad.visible = false; // orders.on_hold
            if (this.textboxHoldReason) this.textboxHoldReason.visible = false; // orders.hold_reason
            if (this.textboxOperationsUser) this.textboxOperationsUser.visible = false; // orders.operations_user
            if (this.textboxOrderedMethod) this.textboxOrderedMethod.visible = false; // orders.ordered_method
            if (this.textboxOrderedDate) this.textboxOrderedDate.visible = false; // orders.ordered_date
            if (this.textboxCopyFromCompany) this.textboxCopyFromCompany.visible = false; // orders.copy_from_company
            if (this.textboxCopyFromOrderId) this.textboxCopyFromOrderId.visible = false; // orders.copy_from_order_id
            if (this.textboxCopyToCompany) this.textboxCopyToCompany.visible = false; // orders.copy_to_company
            if (this.textboxCopyToOrderId) this.textboxCopyToOrderId.visible = false; // orders.copy_to_order_id
            if (this.textboxOrderLength) this.textboxOrderLength.visible = false; // orders.order_length
            if (this.textboxTemperatureMin) this.textboxTemperatureMin.visible = false; // orders.temperature_min
            if (this.textboxTemperatureMax) this.textboxTemperatureMax.visible = false; // orders.temperature_max
            if (this.panelTemperatureRange) this.panelTemperatureRange.visible = false; // temperature range panel
            if (this.textboxOrderValue) this.textboxOrderValue.visible = false; // orders.order_value

            // Hide tabs that contain primarily order/movement data but keep essential ones visible
            // Keep tabStops visible (Pickups and Deliveries) - RESTORED
            // Keep tabRate visible (Rates) - RESTORED for rating fields
            // Keep tabDataFusion visible (DataFusion) - RESTORED for balance due transactions
            // Keep tabCommodity visible (Commodity) - RESTORED for balance due transactions
            // Keep tabInvoiceProcessing visible (Back Office) - RESTORED for balance due transactions
            if (this.tabEquipment) this.tabEquipment.visible = false; // Load Requirements - HIDDEN for balance due
            if (this.tabImages) this.tabImages.visible = false; // Images

            // Balance due uses stopsTable (sourceStop), not ediStopsTable (sourceEdiStop)
            if (this.ediStopsTable) this.ediStopsTable.visible = false;

            // Ensure DataFusion tab is visible for balance due transactions
            if (this.tabDataFusion) this.tabDataFusion.visible = true;

            // Ensure Back Office tab is visible for balance due transactions
            if (this.tabInvoiceProcessing) this.tabInvoiceProcessing.visible = true;

            // Hide errors tabset on DataFusion tab for balance due transactions
            if (this.tabsetErrors) this.tabsetErrors.visible = false;

            // Ensure essential billing components remain visible in EdiBillingRates
            if (this.layoutEdiBillingRates) {
                // Show currency type selector for balance due transactions
                if (this.layoutEdiBillingRates.textboxCurrencyType) {
                    this.layoutEdiBillingRates.textboxCurrencyType.visible = true;
                }
                // Ensure freight charge amount is visible (should be visible by default)
                if (this.layoutEdiBillingRates.textboxFreightCharge) {
                    this.layoutEdiBillingRates.textboxFreightCharge.visible = true;
                }
                // Hide driver extra pay table for balance due transactions
                if (this.layoutEdiBillingRates.tableEdiDriverExtraPay) {
                    this.layoutEdiBillingRates.tableEdiDriverExtraPay.visible = false;
                }
            }

            // Essential fields for billing are kept visible:
            // - textboxPieces (pieces)
            // - textboxWeight (weight)
            // - textboxCurrencyType (linehaul_chg_c) - currency type
            // - textboxFreightCharge (linehaul_chg) - freight charge amount
            // - Rating fields in tabRate
            // - Pickup/Delivery information in tabStops
            // - DataFusion tab (tabDataFusion) - essential for balance due processing
            // - Commodity tab (tabCommodity) - commodity information for billing            console.log("Successfully hid non-essential components for balance due transaction");
        } catch (error) {
            console.warn("Error hiding components for balance due:", error);
        }
    } private onComponentValidationFailed(result: ValidationResult): void {
        const comp = result.component;
        if (comp?.owner instanceof OrderStop)
            comp.owner.onComponentValidationFailed(result);
    }

    dataheaderOnContextMenuCreation(event: ContextMenuCreationEvent) {
        const direction = this.mainDataSource.activeRow?.get("direction");
        const orderId = this.mainDataSource.activeRow?.get("order_id");
        const invoiceId = this.mainDataSource.activeRow?.get("id");
        const ediBillingStatus = this.mainDataSource.activeRow?.get("status");
        const moveId = this.mainDataSource.activeRow?.get("movement_id");
        const invoiceNumberString = this.mainDataSource.activeRow?.get("invoice_no_string") || orderId;

        if (this.activeRow == null || this.sourceEdiBillingDetails.mode !== DataSourceMode.UPDATE) {
            event.preventDefault();
            return;
        }
        event.contextMenu.updateMenuLabels({
            compareHistory: {
                visible: direction == "I" && ediBillingStatus && ediBillingStatus.trim() === "C",
                tooltip: "View results of past compares for this transaction.",
                onClick: async () => this.showCompareHistory(orderId, invoiceId)
            },
            compare: {
                visible: direction == "I" && !StringUtil.isEmptyString(orderId),
                tooltip: "Compare this invoice with another.",
                onClick: async () => await this.openCompareSlideout(invoiceId, orderId, moveId, invoiceNumberString)
            },
            rawData: {
                tooltip: "Display raw data for invoice.",
                onClick: () => this.openRawDataSlideout(true)
            },
            reload: {
                visible: direction === "O",
                tooltip: "Update this record using the order information.",
                onClick: async () => {
                    reloadEdiBilling(this.sourceEdiBillingDetails);
                }
            }
        });
    }
    openRawDataSlideout(rawData: boolean, buttonClicked?: string) {
        const invoiceNo = this.mainDataSource?.activeRow?.get("invoice_no_string");
        const invoiceId = this.mainDataSource?.activeRow?.get("id");
        const templateType = this.mainDataSource?.activeRow?.get("edi_template.format_type");
        const direction = this.mainDataSource?.activeRow?.get("direction");
        const orderId = this.mainDataSource?.activeRow?.get("order_id");
        const title = direction === "I"
            ? (orderId ? `Order ID ${orderId}` : "Inbound Invoice")
            : `Invoice ID ${invoiceNo}`;

        EdiRawDataSlideout.openSlideout({ title, invoiceId, templateType, rawData, buttonClicked, fromViewEdiOrder: false });
    }
    showCompareHistory(orderId: string, invoiceId: string) {
        const layout = Layout.getLayout("lme/datafusion/CompareHistoryList", {
            width: window.innerWidth * .75,
            backgroundColor: "defaultBackground",
            fillHeight: true,
            paddingBottom: 50
        }) as CompareHistoryList;
        new SlideoutDecorator({
            layout,
            title: "Compare History: " + invoiceId,
            fillVerticalSpace: true,
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                layout.textboxTargetOrderId.text = orderId;
                layout.textboxSourceRecordId.text = invoiceId;
                layout.textboxTransactionType.selectedItem = new DropdownItem("I", "DATAFUSION INVOICE");
                await layout.mainDataSource.search({ "target_order_id": orderId, "source_record_id": invoiceId, "transaction_type": "I" });
            }
        });
    }

    async openCompareSlideout(invoiceId: string, orderId: string, moveId: string, invoiceNumberString: string) {
        const status = this.mainDataSource?.activeRow?.get("status");
        let errorMessage = "";
        if (status === COMPARED) {
            errorMessage += "This invoice has already been compared.";
        } else if (status === PENDING) {
            errorMessage += "This invoice has not yet been accepted.";
        } else if (status === REJECTED) {
            errorMessage += "This invoice has been rejected.";
        } else if (status === DISREGARDED) {
            errorMessage += "This invoice has been disregarded.";
        } else if (status === TRANSFERRED) {
            if (!this.mainDataSource?.activeRow?.getBoolean("edibilling_profile.allow_xfer_after_settlement")) {
                CommonDialogs.showDialog("Partner setup does not allow for comparison after a move has been transferred to settlement");
                return;
            } else {
                errorMessage += "The related movement has already been transferred to settlement.";
            }
        }
        if (errorMessage !== "") {
            const proceed = await CommonDialogs.showYesNo(errorMessage + "\n\nWould you still like to continue running the Compare?", "Compare Invoice");
            if (!proceed) return;
            await this.runCompare(invoiceId, orderId, moveId, invoiceNumberString);
        } else {
            await this.runCompare(invoiceId, orderId, moveId, invoiceNumberString);
        }
    }

    async runCompare(invoiceId: string, orderId: string, moveId: string, invoiceNumberString?: string) {
        if (!StringUtil.isEmptyString(invoiceId)) {
            try {
                const compare = new CompareInvoice();
                await compare.compareInvoice(invoiceId, orderId, moveId, invoiceNumberString, this);
            } catch (error) {
                log.error("Error running compare for invoice:", invoiceId, error);
                CommonDialogs.showError(EdiUtility.getCleanErrorMessage(error));
            }
        }
    }

    async textboxCustomerIdOnBlur(event: BlurEvent) {
        if (this.sourceOrders.mode == DataSourceMode.UPDATE && !this.sourceOrders.activeRow.isNull("customer_id")) {
            const ediCustomerRowCount = (await new ModelEdiCustomer().search({ customer_id: this.sourceOrders.activeRow?.get("customer_id") })).actualRowCount;

            this.stopsTable.rows?.forEach(tableRow => {
                const orderStop = tableRow["orderStop"] as OrderStop;
                orderStop.textboxLocationId.googlePlacesProps.customerId = this.sourceOrders.activeRow?.get("customer_id");

                if (this.sourceOrders.activeRow?.get("ordered_method") === "M") {
                    if (ediCustomerRowCount === 0) {
                        orderStop.textboxEdiLocCode.visible = false;
                        orderStop.dfXref.visible = false;
                    }
                    else {
                        orderStop.textboxEdiLocCode.visible = true;
                        if (orderStop.textboxEdiLocCode.text !== "") {
                            orderStop.dfXref.visible = true;
                        }
                    }
                }
            });

            this.ediStopsTable.rows?.forEach(tableRow => {
                const ediStop = tableRow["orderStop"] as EdiStop;
                ediStop.textboxLocationId.googlePlacesProps.customerId = this.sourceOrders.activeRow?.get("customer_id");

                if (this.sourceOrders.activeRow?.get("ordered_method") === "M") {
                    if (ediCustomerRowCount === 0) {
                        ediStop.textboxEdiLocCode.visible = false;
                        ediStop.dfXref.visible = false;
                    }
                    else {
                        ediStop.textboxEdiLocCode.visible = true;
                        if (ediStop.textboxEdiLocCode.text !== "") {
                            ediStop.dfXref.visible = true;
                        }
                    }
                }
            });
        }
    }

    async checkStopsForDistanceChange() {
        const stopdata = this.sourceStop.data;
        let changedText = this.sourceStop.deletedData?.length > 0 ? "removed from" : null;
        for (let i = 0; changedText == null && i < stopdata?.length; i++) {
            if (stopdata[i]._appending) changedText = "added to";
            const changedData = stopdata[i].getChangedData();
            if (changedData?.city_id != null || changedData?.address != null || changedData?.stop_type) changedText = "modified on";
        }
        const locked = this.activeRow?.getBoolean("lock_miles", false)
        if (changedText != null) {
            if (locked) {
                const message = `A stop has been ${changedText} this order which may affect mileage.  Would you like to unlock the mileage?`;
                const unlock = await CommonDialogs.showYesNo(message, "Unlock Miles");
                if (unlock) {
                    this.activeRow.set("lock_miles", "N");
                    this.activeRow.set("bill_distance", null);
                }
            } else {
                this.activeRow?.set("bill_distance", null);
            }
        }
        else {
            this.activeRow?.set("bill_distance", this.activeRow?.originalData["bill_distance"]);
        }
    }

    enableOnHoldComponents(value: boolean) {
        BillingUtil.enableOnHoldComponents(value, this.switchHoldLoad, this.textboxHoldReason);
    }


    private addBlankItemIfBRLTL() {
        if (!this.tabBrltlFreightItems.visible) return;
        const brltlData = BillingUtil.getBRLTLData(this.activeRow);
        if (!BrltlUtil.shouldRateUsingTariff(brltlData)) {
            this.layoutFreightItems.removeBlankFreightItem();
        }
    }

    removeChildDataSources() {
        BillingUtil.removeChildDataSources(this.stopsTable.rows);
        BillingUtil.removeChildDataSources(this.ediStopsTable.rows);
    }

    setCompanySettings(mode: DataSourceMode) {
        const companySettings = LMECompanySettings.getSingleton();
        this.textboxDefMoveType.visible = LMECompanySettings.get().show_movement_type;
        this.isAsset = AssetUtil.isAssetMoveType(this.sourceOrders);
        this.isBrokerageLtlCompany = companySettings.isBrokerageLtl();
    }

    onHoldLoadChange(event) {
        this.textboxHoldReason.visible = event.target.checked;
        this.textboxHoldReason.required = event.target.checked;
        this.labelHoldBadge.visible = event.target.checked;
        if (!event.target.checked && this.activeRow) {
            this.activeRow.set("hold_reason", null);
        }
    }

    setPartnerNameQuickInfo() {
        const row = this.sourceEdiBillingDetails.activeRow;
        setPartnerNameQuickInfo(this.textboxPartnerName, row);
    }

    setupDefMoveType() {
        const assetAndBrokerage = this.activeRow?.get("def_move_type") == "L";
        if (assetAndBrokerage) {
            this.textboxDefMoveType.text = "Asset & Brokerage";
        }
    }

    async setPostingOverrideFields() {
        const orderRow = this.sourceOrders?.activeRow;
        const overrideArriveLate = orderRow.get("override_arrive_late");
        const overrideArriveEarly = orderRow.get("override_arrive_early");
        if (overrideArriveEarly === undefined && overrideArriveLate === undefined) {
            orderRow.set("override_arrive_early", new Date(orderRow.get("stop_origin.sched_arrive_early")));
            if (orderRow.get("stop_origin.sched_arrive_late") !== undefined) {
                orderRow.set("override_arrive_late", new Date(orderRow.get("stop_origin.sched_arrive_late")));
            }
        }

    }


    getRatingDetails(orderId: string) {
        this.layoutEdiBillingRates.sourceRatingDetails.search({ "order_id": orderId }).then(response => {
            this.layoutEdiBillingRates.sourceRatingDetailsSummary.search({ "order_id": orderId }).then(response => {
                this.layoutEdiBillingRates.switchByFreightItem.checked = true;
                this.layoutEdiBillingRates.tableRevSummary.visible = false;
            });
        });
    }

    async doAfterModeChange(event: DataSourceModeChangeEvent) {
        if (this.mainDataSource.mode == DataSourceMode.SEARCH) {
            this.stopsTable.dataSource = null;
            this.ediStopsTable.dataSource = null;
            this.textboxOrderedDate.displayType = DisplayType.DATERANGE;
        } else if (this.stopsTable.dataSource == null) {
            this.stopsTable.dataSource = this.sourceStop;
        } else if (this.ediStopsTable.dataSource == null) {
            this.ediStopsTable.dataSource = this.sourceEdiStop;
        }

        this.makeSubjectOrderAdjustments();
        if (event.oldMode === DataSourceMode.NONE && event.newMode === DataSourceMode.UPDATE) {
            const billType = this.mainDataSource.activeRow?.get("bill_type");
            if (billType === CREDIT_TYPE) {
                this.disableCreditFields(this.tabsetUpdateBilling);
            }
            if (this.sourceOrders.activeRow?.get("credit_override_status") == "P" && this.sourceOrders.activeRow?.get("on_hold", null)) {
                this.enableOnHoldComponents(false);
            }
        }
        this.activeRow?.addAfterFieldUpdateListener((event) => {
            if (event.fieldName === "commodity" && this.textboxCommodityId.dataSource.actionInProgress == null) {
                this.commodityCodeChanged();
            } else if (event.fieldName === "distance") {
                this.updateLabelBillingDistance();
            }
        })
        this.tabImages.visible = event.newMode !== DataSourceMode.SEARCH;
        this.setCompanySettings(event.newMode);
    }

    private updateLabelBillingDistance() {
        BillingUtil.updateLabelBillingDistance(this.layoutOverview, this.layoutEdiBillingRates);
    }

    makeSubjectOrderAdjustments() {
        BillingUtil.makeSubjectOrderAdjustments(this.activeRow, this.textboxCustomerId);
    }

    disableCreditFields(component?: Layout | TableRow | Tabset) {
        const rootComponent = component ?? this;
        BillingUtil.disableCreditFields(rootComponent);
    }

    doBeforeModeChange(event: DataSourceModeChangeEvent) {
        this.hideSuffix = event.newMode != DataSourceMode.ADD;

        this.dataheaderOrders.addlPanelLeft.visible =
            event.newMode === DataSourceMode.ADD ||
            event.newMode === DataSourceMode.UPDATE ||
            event.newMode === DataSourceMode.SEARCH;
        if (event.newMode === DataSourceMode.SEARCH) {
            this.dataheaderOrders.addlPanelLeft.components.forEach(component => {
                component.visible = false;
            });
        }

        this.panelDataHeaderUpdateOnly.visible = event.newMode === DataSourceMode.UPDATE;
        if (event.newMode === DataSourceMode.UPDATE) {
            const titleNumber = !StringUtil.isEmptyString(this.mainDataSource.activeRow?.get("invoice_no_string"))
                ? this.mainDataSource.activeRow?.get("invoice_no_string")
                : this.mainDataSource.activeRow?.get("order_id");
            this.title = "Invoice " + titleNumber;
            this._setPreferredVendorLookupModelData(this.sourceOrders.activeRow);
        }

        this.tabOverview.visible = event.newMode === DataSourceMode.UPDATE || event.newMode === DataSourceMode.NONE;
        this.tabInvoiceProcessing.visible = event.newMode !== DataSourceMode.ADD;
        this.tabDataFusion.visible = event.newMode !== DataSourceMode.ADD;
        this.tabStops.visible = event.newMode !== DataSourceMode.SEARCH;
    }

    commodityCodeChanged() {
        if (this.activeRow != null) {
            const commodityCleared = this.activeRow.isNull("commodity");
            const lookupData = commodityCleared ? null : this.textboxCommodityId.getFirstLookupModelData();
            const hazmatModelRow = commodityCleared ? null : new ModelRow(this.textboxHazmatCode.lookupModel, false, { id: lookupData?.get("hazmat_number") });

            this.showHazmatWarning(lookupData?.get("hazmat_number") != null);
            this.sourceEdiBillingDetails.activeRow.setLookupModelData("hazmat_code", hazmatModelRow);
            this.sourceEdiBillingDetails.activeRow.set("hazmat_code", lookupData?.get("hazmat_number"));
            // this.sourceEdiBillingDetails.activeRow.set("commodity_descr", lookupData?.get("descr"));
            this.sourceEdiBillingDetails.activeRow.set("hazmat", lookupData?.get("hazmat", "N"));
            this.sourceEdiBillingDetails.activeRow.set("temperature_min", lookupData?.get("temperature_min"));
            this.sourceEdiBillingDetails.activeRow.set("temperature_max", lookupData?.get("temperature_max"));
            this.sourceEdiBillingDetails.activeRow.set("placard_required", lookupData?.get("placard_required", null));
        }
    }

    getBRLTLData() {
        return BillingUtil.getBRLTLData(this.activeRow);
    }

    showHazmatWarning(hazmat: boolean) {
        BillingUtil.showHazmatWarning(this.panelCommodityHazmat, this.labelHazmatCommodity, hazmat);
    }

    buttonAddAnotherStopOnDataDisplay(event: DataDisplayEvent) {
        BillingUtil.buttonAddAnotherStopOnDataDisplay(event);
    }

    //** This is an event handler for stopsTable onRowBeforeSave */
    tableStopBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow: TableRow = event.target as TableRow;
        const modelRow: ModelRow = tableRow.data;
        const orderStopLayout: OrderStop = event.target["orderStop"] as OrderStop;
        if (orderStopLayout?.commentsAndRefNbrs) {
            modelRow.set("stop_notes", orderStopLayout.commentsAndRefNbrs.sourceStopNote?.data || []);
            modelRow.set("stop_reference_numbers", orderStopLayout.commentsAndRefNbrs.sourceReferenceNumber?.data || []);
            const changedData = modelRow.getChangedData();
            if (AppointmentUtil.apptConfirmCheck(changedData, modelRow))
                orderStopLayout.getAppointmentChangedRow();
            else {
                modelRow.set("appointment_change", null);
            }
        }
    }

    /** This is an event handler for the onRowDisplay event of stopsTable.  */
    stopsTableOnRowDisplay(event) {
        const tableRow: TableRow = event.getTableRow();
        tableRow.allowDelete = tableRow.index != 0;
        const labelSequence = (tableRow.findComponentById("labelSequence")) as Label;
        labelSequence.text = (tableRow.index + 1).toString();
        const buttonManageStops = (tableRow.findComponentById("buttonManageStops")) as Panel;
        buttonManageStops.visible = this.sourceStop.mode != DataSourceMode.ADD;

        const layoutOrderStop = tableRow.findComponentById("layoutOrderStop") as OrderStop;
        const billType = this.mainDataSource.activeRow?.get("bill_type");
        if (billType === CREDIT_TYPE) {
            this.disableCreditFields(tableRow);
        }
        this.sourceStop.preventChangeNotifications = true;
        tableRow["orderStop"] = layoutOrderStop;
        layoutOrderStop.calculateRates = () => this.checkStopsForDistanceChange().then(() => {
            this.layoutEdiBillingRates.calculateRates(false, false)
        });
        layoutOrderStop.commodityChanged = () => {
            this.setCommodity();
        }
        layoutOrderStop.initializeBilling(false, tableRow, this.sourceOrders, this.sourceStop, this.sourceEdiBillingDetails.mode, this.sourceEdiBillingDetails);
        layoutOrderStop.loadingRecurringOrder = this.loadingRecurringOrder;
        layoutOrderStop.invokedFromEDI = this.getInvokedFromEdi();
        if (this.getInvokedFromEdi()) {
            layoutOrderStop.setStopDateTypeForEdi();
        }
        layoutOrderStop.setFreeFormVisibility();
        if (this.getInvokedFromEdi() || this.loadingRecurringOrder || (this["quote_id"] && !this.hasPortalContactInfo(tableRow.data))) {
            layoutOrderStop.doAfterLocationIdChanged();
        }
        if (tableRow.index == 0) {
            if (this.isBrokerageLtlCompany) {
                this.layoutShipperPONums.initialize({
                    "stopSource": this.sourceStop,
                    "firstStopRow": tableRow.data
                });
            }
            layoutOrderStop.switchPalletsRequired.displayData(this.sourceOrders.activeRow, null, 0);
            layoutOrderStop.textboxPalletsHowMany.displayData(this.sourceOrders.activeRow, null, 0);
        }
        if (layoutOrderStop.commentsAndRefNbrs.enabled == false) {
            const commentsTable = layoutOrderStop.commentsAndRefNbrs.tableStopComments;
            commentsTable.allowEdit = false;
            const tableRefNbrs = layoutOrderStop.commentsAndRefNbrs.tableStopReferenceNumbers;
            tableRefNbrs.allowEdit = false;
            layoutOrderStop.commentsAndRefNbrs.tabsetStopNotesRefNos.expandAll();
        }
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            tableRow.data.addAfterFieldUpdateListener(async (event: FieldUpdateEvent) => {
                if (event.fieldName === "sched_arrive_early" && (this.sourceOrders.mode == DataSourceMode.ADD
                    || "A" == this.sourceOrders.activeRow?.getString("status")) && !this.sourceOrders.actionInProgress) {
                    await this.setConversionDates(this.getConversionDateFromStops(this.stopsTable));
                }
            });
        }
        this.sourceStop.preventChangeNotifications = false;
        layoutOrderStop.checkLocationIsTrailerPool = (usePool: boolean) => { this.setPreloadedTrailerLookupInfo(usePool) };
        this.enableCaptions(layoutOrderStop);
        layoutOrderStop.textboxStopType.required = false;
        layoutOrderStop.textboxSchedArriveEarly.required = false;
        layoutOrderStop.citystate.required = false;
        layoutOrderStop.textboxDriverLoadUnload.required = false;
    }

    /** This is an event handler for the onRowDisplay event of ediStopsTable.  */
    ediStopsTableOnRowDisplay(event) {
        const tableRow: TableRow = event.getTableRow();
        tableRow.allowDelete = tableRow.index != 0;
        const labelSequence = (tableRow.findComponentById("labelEdiSequence")) as Label;
        labelSequence.text = (tableRow.index + 1).toString();
        const buttonManageStops = (tableRow.findComponentById("buttonManageEdiStops")) as Panel;
        buttonManageStops.visible = this.sourceEdiStop.mode != DataSourceMode.ADD;

        const layoutEdiStop = tableRow.findComponentById("layoutEdiStop") as EdiStop;
        const billType = this.mainDataSource.activeRow?.get("bill_type");
        if (billType === CREDIT_TYPE) {
            this.disableCreditFields(tableRow);
        }
        this.sourceEdiStop.preventChangeNotifications = true;
        tableRow["orderStop"] = layoutEdiStop;
        layoutEdiStop.calculateRates = () => this.checkStopsForDistanceChange().then(() => {
            this.layoutEdiBillingRates.calculateRates(false, false)
        });
        layoutEdiStop.initialize(false, tableRow.index, this.sourceOrders, this.sourceEdiStop);
        layoutEdiStop.loadingRecurringOrder = this.loadingRecurringOrder;
        layoutEdiStop.setFreeFormVisibility();
        if (this.getInvokedFromEdi() || this.loadingRecurringOrder || (this["quote_id"] && !this.hasPortalContactInfo(tableRow.data))) {
            layoutEdiStop.doAfterLocationIdChanged();
        }
        if (tableRow.index == 0) {
            if (this.isBrokerageLtlCompany) {
                this.layoutShipperPONums.initialize({
                    "stopSource": this.sourceEdiStop,
                    "firstStopRow": tableRow.data
                });
            }
            layoutEdiStop.switchPalletsRequired.displayData(this.sourceOrders.activeRow, null, 0);
            layoutEdiStop.textboxPalletsHowMany.displayData(this.sourceOrders.activeRow, null, 0);
        }
        if (layoutEdiStop.commentsAndRefNbrs.enabled == false) {
            const commentsTable = layoutEdiStop.commentsAndRefNbrs.tableStopComments;
            commentsTable.allowEdit = false;
            const tableRefNbrs = layoutEdiStop.commentsAndRefNbrs.tableStopReferenceNumbers;
            tableRefNbrs.allowEdit = false;
            layoutEdiStop.commentsAndRefNbrs.tabsetStopNotesRefNos.expandAll();
        }
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            tableRow.data.addAfterFieldUpdateListener(async (event: FieldUpdateEvent) => {
                if (event.fieldName === "sched_arrive_early" && (this.sourceOrders.mode == DataSourceMode.ADD
                    || "A" == this.sourceOrders.activeRow?.getString("status")) && !this.sourceOrders.actionInProgress) {
                    await this.setConversionDates(this.getConversionDateFromStops(this.ediStopsTable));
                }
            });
        }
        this.sourceEdiStop.preventChangeNotifications = false;
        this.enableCaptions(layoutEdiStop);
        layoutEdiStop.textboxSchedArriveEarly.required = false;
        layoutEdiStop.textboxDriverLoadUnload.required = false;
        layoutEdiStop.textboxEdiLocCode.enabled = false;
    }

    async validateStops(event: TableContentsChangedEvent) {
        const stopData = this.sourceStop.data;
        let errorMessage = null;

        if (stopData.length < 2) {
            errorMessage = "An order must have at least two stops.";
        } else if (!stopData.some(stop => stop.get("stop_type") === "PU")) {
            errorMessage = "Cannot delete the only pickup stop.";
        } else if (!stopData.some(stop => stop.get("stop_type") === "SO")) {
            errorMessage = "Cannot delete the only delivery stop.";
        } else if (stopData[stopData.length - 1].get("stop_type") !== "SO") {
            errorMessage = "The delivery stop should be the last stop.";
        }

        if (errorMessage) {
            await CommonDialogs.showError(errorMessage, "Cannot Delete Stop");
            event.preventDefault();
            const orderId = this.sourceOrders.activeRow?.get("id");
            if (orderId) {
                await this.sourceEdiBillingDetails.search({ "orders.id": orderId });
            }
        }
    }

    validateStopsBeforeSave(event: DataSourceValidationEvent): void {
        if (event.dataSource.mode !== DataSourceMode.ADD && event.dataSource.mode !== DataSourceMode.UPDATE) {
            return;
        }
        const stopData = this.sourceStop.data;
        BillingUtil.validateStopsBeforeSave(event, stopData, this.stopsTable);
    }

    private enableCaptions(comp: any) {
        BillingUtil.enableCaptions(comp);
    }

    private revenueCodeChanged(newValue: any) {
        Api.search("lme/dispatch/calculate-order-mode",
            {
                "mode_locked": false,
                "revenue_code_id": newValue,
                "order_type_id": this.mainDataSource.activeRow?.get("order_type_id"),
                "current_mode": this.sourceOrders.activeRow?.get("order_mode")
            }).then(response => {
                const data = response.data[0];
                const newMode = data["mode"];
                this.sourceOrders.activeRow.set("order_mode", newMode);
            });
    }

    private hasPortalContactInfo(data: ModelRow) {
        return BillingUtil.hasPortalContactInfo(data);
    }

    private orderTypeChanged(newValue: any) {
        Api.search("lme/dispatch/calculate-order-mode",
            {
                "mode_locked": false,
                "revenue_code_id": this.mainDataSource.activeRow?.get("revenue_id"),
                "order_type_id": newValue,
                "current_mode": this.sourceOrders.activeRow?.get("order_mode")
            }).then(response => {
                const data = response.data[0];
                const newMode = data["mode"];
                this.sourceOrders.activeRow.set("order_mode", newMode);
            });
    }

    displayBillingUsersSlideout() {
        const urs = new UsersResponsibilitySlideoutTrans("lme/ar/BillingUsers", this.getBillingUsersSlideoutTitle(), this.getBillingUsersIdFilter(),
            this.getBillingUsersSharedFieldNames(), this.sourceEdiBillingDetails, this.sourceResponsibleHist, this.mainDataSource.activeRow?.get("bill_type") === CREDIT_TYPE);
        urs.show(this.mainDataSource.activeRow?.get("bill_type") !== CREDIT_TYPE);
    }

    private getBillingUsersSlideoutTitle(): string {
        const name = this.sourceEdiBillingDetails.activeRow?.get("order_id");
        return "Billing Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getBillingUsersIdFilter(): any {
        const id = this.sourceEdiBillingDetails.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }

    private getBillingUsersSharedFieldNames(): string[] {
        return ["operations_user", "agent", "entered_user_id", "billing_user_id"];
    }

    sourceEdiBillingDetailsAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            //have to set these fields when in search mode because they aren't bound fields for the main layout
            for (const field of this.getBillingUsersSharedFieldNames()) {
                row.set(field, dataSource.searchRow.get(field));
            }

            //have to add hierarchy search data to the filter manually, since there are no bound components for those records
            if (ArrayUtil.isEmptyArray(this.sourceResponsibleHist.data))
                return;
            row.addLinkedModel({ model: this.sourceResponsibleHist.url, rows: [this.sourceResponsibleHist.data[0]] });
        }

        // Set up tooltips for EDI fields
        const ediRow = this.sourceEdiBillingDetails.activeRow;
        if (ediRow) {
            EdiBillingUtility.makeTooltipContent(this.txtCorrectionIndicator, ediRow);
            EdiBillingUtility.makeTooltipContent(this.txtServiceCode, ediRow);
        }
    }

    textboxPnnLoadboardUserIdBeforeLookupModelSearch(event) {
        event.filter.web_user_type = "U";
    }

    public static navigateTo(id: string, newTab = true): void {
        ViewBilling.navigateTo({ key: id, newTab: newTab });
    }


    stopTableOnEditLoaded(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const thisOrderStop = tableRow["orderStop"] as OrderStop;
            const editOrderStop = rowDecorator.layout as OrderStop;
            if (this.sourceOrders.isAddingOrUpdating()) {
                editOrderStop.commentsAndRefNbrs.tableStopComments.dataSource.mode = DataSourceMode.UPDATE;
                editOrderStop.commentsAndRefNbrs.tableStopReferenceNumbers.dataSource.mode = DataSourceMode.UPDATE;
            }
            editOrderStop.mainDataSource.activeRow?.set("stop_notes", thisOrderStop?.commentsAndRefNbrs.tableStopComments.data);
            editOrderStop.mainDataSource.activeRow?.set("reference_numbers", thisOrderStop?.commentsAndRefNbrs.tableStopReferenceNumbers.data);
            editOrderStop.initialize(true, tableRow, this.sourceOrders, this.sourceStop);
            editOrderStop.slideOutTableRow = tableRow;

            rowDecorator.onSave = this.stopSlideoutOnSave(rowDecorator, tableRow);
            rowDecorator.btnDelete.visible = false;
            rowDecorator.layout.width = window.innerWidth * .75;
            rowDecorator.labelTitle.text = "Order Entry Stop Details - " + this.title;
            rowDecorator.multiButton.dropdownItems = null;
        };
    }

    ediStopTableOnEditLoaded(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const thisOrderStop = tableRow["orderStop"] as EdiStop;
            const editOrderStop = rowDecorator.layout as EdiStop;
            if (this.sourceOrders.isAddingOrUpdating()) {
                editOrderStop.commentsAndRefNbrs.tableStopComments.dataSource.mode = DataSourceMode.UPDATE;
                editOrderStop.commentsAndRefNbrs.tableStopReferenceNumbers.dataSource.mode = DataSourceMode.UPDATE;
            }
            editOrderStop.mainDataSource.activeRow?.set("stop_notes", thisOrderStop?.commentsAndRefNbrs.tableStopComments.data);
            editOrderStop.mainDataSource.activeRow?.set("reference_numbers", thisOrderStop?.commentsAndRefNbrs.tableStopReferenceNumbers.data);
            editOrderStop.initialize(true, tableRow.index, this.sourceOrders, this.sourceEdiStop);
            editOrderStop.slideOutTableRow = tableRow;

            rowDecorator.btnDelete.visible = false;
            rowDecorator.layout.width = window.innerWidth * .75;
            rowDecorator.labelTitle.text = "Edi Stop Details - " + this.title;
            rowDecorator.multiButton.dropdownItems = null;
        };
    }

    stopSlideoutOnSave(rowDecorator: EditRowDecorator, tableRow: TableRow): (updatedData: ModelRow | any) => void {
        const orderStop = (rowDecorator.layout as OrderStop);
        const thisOrderStop = tableRow["orderStop"] as OrderStop;
        return async (_updatedData: ModelRow | any) => {
            thisOrderStop.textboxLocationId["_lookupModelDataForUpdate"] = orderStop.textboxLocationId["_lookupModelDataForUpdate"]; // syncs lookupModelDataForUpdate which is used in updateBoundData in Location.ts
            tableRow.saveChangesFromEdit(tableRow.data);
            if (tableRow.index == 0) {
                this.sourceOrders.activeRow.set("pallets_how_many", orderStop.textboxPalletsHowMany.text);
                this.sourceOrders.activeRow.set("pallets_required", true === orderStop.switchPalletsRequired.checked ? "Y" : "N");
            }
            orderStop.commentsAndRefNbrs.saveChangesFromEdit(tableRow["orderStop"].commentsAndRefNbrs);
            thisOrderStop.textboxLocationId.displayData(tableRow.data, null, 0);
            thisOrderStop.setLocationTextAfterLocation(thisOrderStop.textboxLocationId.textCombined, tableRow.data, "location_id");
            thisOrderStop.calculateRates();
            thisOrderStop.activeRow.set("initial_appointment_change", null); // reset in scenario if user exits slideout and makes another update on main screen
        };
    }

    viewLoadTenders() {
        const id = this.sourceOrders.activeRow?.data?.["id"];
        log.debug("id", id);
        if (id) {
            const layout = Layout.getLayout("lme/datafusion/OrderLoadTenderHistory", {
                maxWidth: 900,
                width: window.innerWidth * .60,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as OrderLoadTenderHistory;

            new SlideoutDecorator({
                layout: layout,
                title: "Tenders - Order " + id,
                fillVerticalSpace: true,
                doAfterSlideIn: (decorator: SlideoutDecorator) => layout.mainDataSource.search({ order_id: id })
            });

            layout.orderId = id;
        }
    }

    viewShipmentStatus() {
        const id = this.sourceOrders.activeRow?.data?.["id"];
        const companyId = this.sourceOrders.activeRow?.data?.["company_id"];
        if (id) {
            const layout = Layout.getLayout("lme/dispatch/ShipmentStatusSlideout", {
                //maxWidth: 900,
                width: window.innerWidth * .70,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as ShipmentStatusSlideout;

            new SlideoutDecorator({
                layout,
                title: `Order Number ${id}`,
                fillVerticalSpace: true,
                addlComponents: [layout.saveButton],
                onClose: () => {
                    this.sourceOrders.search({ id: id });
                },
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    layout.tabsetDataFusion.selectedIndex = layout.tabsetDataFusion.indexOf(layout.tabOutbound);
                    layout.sourceEdistatus.search({ order_id: id });
                    layout.sourceEdiOrder.search({ order_id: id });
                    layout.sourceEdiMovementOrder.search({ order_id: id, company_id: companyId });
                    layout.saveButton.addClickListener(async event => {
                        if (decorator.validateSimple()) {
                            await layout.saveChanges();
                        }
                    });
                },
                doAfterSlideOut: (decorator: SlideoutDecorator) => {
                    this.sourceOrders.search({ id: id });
                }
            });
        }
    }

    setupPreloadedTrailerLookup() {
        const data = this.sourceStop?.data[0]?.get("location");
        if (data != null && data.length > 0) {
            const isTrailerPool = data[0].is_trailer_pool;
            const usePool = isTrailerPool == "Y" ? true : false;
            this.setPreloadedTrailerLookupInfo(usePool);
        }
    }

    setPreloadedTrailerLookupInfo(usePool: boolean) {
        if (usePool) {
            this.textboxPreloadTrailerId.lookupModel = "lme/dispatch/trailer-pool";
            this.textboxPreloadTrailerId.lookupModelLayout = "lme/dispatch/asset/TrailerPoolLookup"
            this.textboxPreloadTrailerId.lookupModelDisplayField = "trailer_id";
            this.textboxPreloadTrailerId.lookupModelResultField = "trailer_id";
            this.textboxPreloadTrailerId.lookupModelAllowFreeform = true;
            this.textboxPreloadTrailerId.placeholder = "Trailer Pool";
        } else {
            this.textboxPreloadTrailerId.lookupModel = "lme/dispatch/asset/trailer-lookup";
            this.textboxPreloadTrailerId.lookupModelLayout = "lme/dispatch/asset/TrailerLookup"
            this.textboxPreloadTrailerId.lookupModelDisplayField = "id";
            this.textboxPreloadTrailerId.lookupModelResultField = "id";
            this.textboxPreloadTrailerId.lookupModelAllowFreeform = false;
            this.textboxPreloadTrailerId.placeholder = "All Trailers";
        }
    }

    createPostToLoadBoardDialog() {
        const dialog = new Dialog({ okVisible: true });
        const lblCaptionLine1 = new Label({
            text: "Orders on hold may not be posted to the load boards.",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        });
        const lblCaptionLine2 = new Label({
            text: "Remove the hold to post this order.",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        })
        const pnlCaption = new Panel({
            marginRight: 8,
            verticalAlign: VerticalAlignment.TOP,
            fillHeight: true,
            rowBreak: false
        })
        pnlCaption.add(lblCaptionLine1, lblCaptionLine2);

        const pnlContainer = new Panel({ fillRow: true, maxWidth: 550 });
        pnlContainer.add(pnlCaption);
        dialog.add(pnlContainer);
        return dialog.show();

    }

    createUpdateDeniedDialog() {
        const dialog = new Dialog({ okVisible: true });
        const lblCaptionLine1 = new Label({
            text: "Order has been billed and you do not have permission to update an order " +
                "after the order has been billed",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        });
        const pnlCaption = new Panel({
            marginRight: 8,
            verticalAlign: VerticalAlignment.TOP,
            fillHeight: true,
            rowBreak: false
        });
        pnlCaption.add(lblCaptionLine1);
        const pnlContainer = new Panel({ fillRow: true, maxWidth: 550 });
        pnlContainer.add(pnlCaption);
        dialog.add(pnlContainer);
        return dialog.show();
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.edi.EntryEdiBilling", "<ext> search id ='" + this.mainDataSource.activeRow?.get("id") + "'");
    }

    public get doAfterAction(): () => void {
        return this._doAfterAction;
    }

    public set doAfterAction(value: () => void) {
        this._doAfterAction = value;
    }

    buttonOrderHistoryOnClick(event: ClickEvent) {
        const props: OrderPostHistProps = {
            orderType: "ORDER",
            orderId: this.activeRow?.get("order_id"),
            movementId: this.sourceOrders.activeRow?.get("curr_movement_id"),
            isBilled: !this.activeRow.isNull("bill_date") || !this.activeRow.isNull("transfer_date"),
        }
        OrderPostHist.showInSlideout(
            props, DataSourceMode.UPDATE, this.buttonOrderHistory);
    }

    equipItemTableOnRowDisplay(event) {
        const row: TableRow = event.getTableRow();
        let equipType = (row.findComponentById("textboxMatchEquipmentTypeId")) as Textbox;
        if (equipType == null) {
            equipType = (row.findComponentById("textboxMatchEquipmentTypeIdAsset")) as Textbox;
        }
        if (equipType != null && row.data.get("equipment_match_id") != undefined) {
            equipType.tooltipDynamicContent = () => {
                const panel = new Panel({ padding: 0, margin: 0 });
                panel.add(new Label({ text: "Match ID", fontSize: "medium", fontBold: true }));
                panel.add(new Label({ text: row.data.get("equipment_match_id") }));
                return panel;
            }
        }

        if (this.tableEquipmentRequirementsAsset.visible && this.tableEquipmentRequirementsAsset.quickAddRow === row && row != null) {
            this.updateStopNumberDropdown();
        }
    }

    updateStopNumberDropdown() {
        const textboxStopNumber = this.tableEquipmentRequirementsAsset?.quickAddRow?.findComponentById("textboxStopNumber") as Textbox;
        if (textboxStopNumber) {
            const items = [];
            items.push(ALL_ITEM);
            Array.from({ length: this.mainDataSource.activeRow?.get("direction") === OUTBOUND ? this.stopsTable.rowCount : this.ediStopsTable.rowCount }, (_, i) => i + 1).forEach(stopNumber => items.push(stopNumber));
            textboxStopNumber.items = items;
        }
    }

    public set invokedFromEdi(value: boolean) {
        this._invokedFromEdi = value;
    }

    public getInvokedFromEdi(): boolean {
        return this._invokedFromEdi;
    }
    public get holdReasonChanged(): boolean {
        return this._holdReasonChanged;
    }
    public set holdReasonChanged(value: boolean) {
        this._holdReasonChanged = value;
    }
    async stopsTableOnContentsChanged(event: TableContentsChangedEvent) {
        if (TableAction.DELETE == event.getAction()) {
            await this.validateStops(event);
            if (event.defaultPrevented) {
                this.checkStopsForDistanceChange().then(() => {
                    this.layoutEdiBillingRates.calculateRates(false, false);
                });
            }
        }
        this.updateStopNumberDropdown();
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            if (this.sourceOrders.mode !== DataSourceMode.ADD && "A" == this.sourceOrders.activeRow?.getString("status")) {
                await this.setConversionDates(this.getConversionDateFromStops(this.stopsTable));
            }
        }
    }

    private validateRespFilteringDuringAdd(event: DataSourceValidationEvent, buttonUsers: Button) {
        if (event.dataSource.activeRow?.get("rf_entry_code_required", false) !== true)
            return;
        let rfDataSource: DataSource = null;
        if (event.childDataSources != null) {
            for (const childDataSource of event.childDataSources) {
                if (childDataSource.url === "lme/general/responsible-hist") {
                    rfDataSource = childDataSource;
                    break;
                }
            }
        }
        if (rfDataSource != null) {
            for (const row of rfDataSource.data) {
                if (row.get("responsible_role", null) === "E" &&
                    (row.get("hierarchy_level_1", null) != null || row.get("hierarchy_level_2", null) != null || row.get("hierarchy_level_3", null) != null ||
                        row.get("hierarchy_level_4", null) != null || row.get("hierarchy_level_5", null) != null)) {
                    //an entry row is present, return without adding anything to the validation results
                    return;
                }
            }
        }
        const message = "A responsibility record with role 'Entry' must be specified";
        event.validationResults.push({
            isValid: false,
            validationMessage: message,
            caption: message,
            component: buttonUsers
        });
    }

    private get saveButton(): SaveButton {
        return this.dataHeader?.["saveButton"];
    }

    public displayOrderCreatedToast(orderId?: string) {
        const id = orderId ?? this.sourceOrders?.activeRow?.get("id", null);
        const toast = Toast.showToast(this.getOrderCreatedToast(id, "created", () => this.orderToastLinkOnClick(id, toast)), null);
    }

    private orderToastLinkOnClick(key: string, toast: Toast) {
        ViewBilling.navigateTo({ key, newTab: false });
        toast.dismiss();
    }

    private getOrderCreatedToast(orderId: string, action: string, doOnLinkClick: () => void): Panel {
        const labelProps = {
            color: "white",
            fontSize: "xlarge",
            rowBreak: false,
            paddingLeft: 1,
            paddingRight: 1,
            marginLeft: 0,
            marginRight: 0
        };
        let labels: Label[] = [];
        if (!StringUtil.isEmptyString(orderId)) {
            labels = [
                new Label({ ...labelProps, text: "Order " }),
                new Label({
                    ...labelProps,
                    style: { textDecoration: "underline" },
                    text: orderId,
                    onClick: event => doOnLinkClick()
                }),
                new Label({ ...labelProps, text: " " + action + "." })
            ];
        } else {
            labels = [
                new Label({ ...labelProps, text: "Order not " + action + "." })
            ];
        }
        return new Panel({ align: HorizontalAlignment.CENTER, fillRow: true, components: labels });
    }

    imageAddHandlingReqOnClick() {
        OrderHandlingRequirementsSlideout.showSlideout({
            hdrDataSource: this.sourceBrltlOrderHdrXFgp,
            hdrChanged: (row: ModelRow, type: ModelDataChangeType) => this.hdrChanged(row, type),
            onSave: (hasChanged: boolean) => {
                this.layoutHandlingReqs.populateHdrs(this.sourceBrltlOrderHdrXFgp.data, true, hasChanged).then(() => {
                    this.sourceBrltlOrderHdrXFgp.notifyHasChangedComponents(true);
                });
            },
            invokedFromEdi: false
        })
    }

    hdrChanged(hdr: ModelRow, type: ModelDataChangeType) {
        if (type == ModelDataChangeType.ADD) {
            hdr.set("fgp_uid", this.sourceOrders.activeRow?.get("fgp_uid"));
            hdr.set("order_id", this.sourceOrders.activeRow?.get("id"));
            hdr._appending = true;
            this.sourceBrltlOrderHdrXFgp.addRow(hdr, this.sourceBrltlOrderHdrXFgp.data.length, false);
        } else if (type == ModelDataChangeType.DELETE) {
            this.sourceBrltlOrderHdrXFgp.data.forEach(row => {
                if (row?.data["hdr_uid"] == hdr.data["hdr_uid"]) {
                    this.sourceBrltlOrderHdrXFgp._deleteTableRow(row, true);
                }
            });
        } else if (type == ModelDataChangeType.UPDATE) {
            this.sourceBrltlOrderHdrXFgp.data.forEach((row, index) => {
                if (row?.data["hdr_uid"] == hdr.data["hdr_uid"]) {
                    row.setValues({
                        "text_value": hdr.data["text_value"],
                        "integer_value": hdr.data["integer_value"],
                        "float_value": hdr.data["float_value"],
                        "time_value": hdr.data["time_value"],
                        "date_value": hdr.data["date_value"]
                    });
                }
            });
        }

    }

    async validateProcessStatus(orderId: string, mode: string): Promise<any> {
        return await Api.post("lme/datafusion/status-processing", { order_id: orderId, datasource_mode: mode });
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    sourceBrltlOrderHdrXFgpAfterExecution(event: DataSourceExecutionEvent) {
        this.layoutHandlingReqs.populateHdrs(event.dataSource.data, true);
    }

    getStopDataSource(): DataSource {
        return this.sourceStop;
    }

    getHdrXFgpDataSource(): DataSource {
        return this.sourceBrltlOrderHdrXFgp;
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxOrderType. */
    textboxOrderTypeBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
    }

    displaySuccessfulCancelToastMessage(orderId: string, movementId: string) {
        const lbl = new Label({ text: `Success!\n\rThe load posting for Order ${orderId} has been canceled`, align: HorizontalAlignment.CENTER, fontBold: true, fontSize: "xxlarge" });
        Toast.showToast(lbl, { backgroundColor: "primary" });
    }

    displayFailedCancelToastMessage(orderId: string, successfulCancellationsVendors: string, failedCancellationsVendors: string) {
        if (!StringUtil.isEmptyString(successfulCancellationsVendors) && !StringUtil.isEmptyString(failedCancellationsVendors)) {
            const partialSuccessful = `Success! The load posting for ${orderId} has been canceled for: ${successfulCancellationsVendors}`;
            Toast.showToast(partialSuccessful, null, { persist: true });

            const partialFailure = `The load posting for Order ${orderId} was not canceled for: ${failedCancellationsVendors}. Please retry directly on the load board site.`
            Snackbar.showWarningSnackbar(partialFailure);
        }
        else if (!StringUtil.isEmptyString(successfulCancellationsVendors)) {
            const loadBoardCancellationSuccessful = `${successfulCancellationsVendors} cancellation of load posting was successful.`;
            Toast.showToast(loadBoardCancellationSuccessful, null, { persist: true });
        } else if (!StringUtil.isEmptyString(failedCancellationsVendors)) {
            const loadBoardCancellationFailed = `${failedCancellationsVendors} cancellation of load posting was unsuccessful. Please retry directly on the load board site.`;
            Snackbar.showWarningSnackbar(loadBoardCancellationFailed);
        } else {
            // This condition occurs when there is not a matching load_board_trx record for the order_id and movement_id.
            const loadBoardCancellationFailed = `Cancellation of load posting was unsuccessful. Please retry directly on the load board site.`;
            Snackbar.showWarningSnackbar(loadBoardCancellationFailed);
        }
    }


    public set currencyFromProfile(value: string) {
        this._currencyFromProfile = value;
    }

    private getConversionDateFromStops(table: Table): Date {
        const conversionBy: string = CurrencySettings.getConversionBy();
        const numberOfStops = table.rows.length;
        if (numberOfStops > 0) {
            const stop: TableRow = "S" == conversionBy ? table.rows[0] : table.rows[numberOfStops - 1];
            return stop.data.get("actual_arrival") ?? stop.data.get("sched_arrive_early") ?? new Date();
        }
        return new Date();
    }

    private async setConversionDates(conversionDate: Date) {
        const dataRow: ModelRow<any> = this.sourceOrders.activeRow ?? this.sourceOrders.data[0];
        if (conversionDate) {
            EdiBilling.orderCurrencies.forEach(fieldName => {
                const currency = dataRow.getCurrency(fieldName);
                if (currency && conversionDate != currency.getConversionDate()) {
                    currency.setConversionDate(conversionDate);
                    dataRow.set(fieldName, currency);
                }
            });
            await this.layoutEdiBillingRates.calculateRates(false, false);
            this.layoutEdiBillingRates.layoutMovementRates.validateCarrierPayOverrideDates();
        }
    }

    public getCurrencyFromProfile(): string {
        return this._currencyFromProfile;

    }

    async getTransactionalCurrency(): Promise<string> {
        let transactionCurrency = CurrencySettings.getTransactionalCurrency();
        const partner = this.sourceOrders.activeRow?.getString("partner_id");
        const version = this.sourceOrders.activeRow?.getString("partner_version");
        if (this.getInvokedFromEdi() && this.getCurrencyFromProfile()) {
            transactionCurrency = this.getCurrencyFromProfile();
        } else if (partner && version) {
            const profile = await new ModelEdiorderProfile().searchSingle({
                partner_id: partner,
                version: version
            });
            if (profile.data.currency_type_id) {
                transactionCurrency = profile.data.currency_type_id;
            }
        }
        return transactionCurrency;
    }

    textboxCommodityIdOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && StringUtil.isEmptyString(event.newValue)) {
            this.activeRow.set("commodity_descr", null);
            this.textboxCommodity.text = null;
        }
    }

    async textboxCommodityIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const descr = event.selection.get("descr");
            this.activeRow.set("commodity_descr", descr);
            this.textboxCommodity.text = descr;
            await this.setCommodityQuickInfo();
        }
    }

    async getIsHazmatCommodity(): Promise<boolean> {
        const commodityId = this.sourceEdiBillingDetails.activeRow?.get("commodity");
        if (commodityId === null || commodityId === undefined) return;
        const response = await Model.searchSingleRecord("lme/dispatch/commodity", { id: commodityId });
        if (response != null) {
            return response?.get("is_hazmat") === "Y";
        } else {
            return false;
        }
    }

    // Used in onClickListener for Manage Stops button
    private _manageStops() {
        if (this.sourceStop.hasChanged()) {
            Snackbar.showWarningSnackbar("Save previous stop edits before opening Manage Stops.");
            return;
        }
        const slideout = new RearrangeStopSlideout("O", this.mainDataSource?.activeRow?.get("order_id"));
        slideout.showSlideout(
            () => { },
            (canceled: boolean) => {
                if (!canceled) {
                    Toast.showSuccessToast("Changes were saved successfully.");
                    ViewBilling.navigateTo({ key: this.activeRow?.get("id"), newTab: false });
                }

            }
        );
    }

    textboxOnHoldReasonCodeBeforeLookupModelSearch(event) {
        event.filter.code_type = "%B%";
        event.filter.is_active = "Y";
    }

    afterBillingExecution(event) {

        const orderId = this.sourceEdiBillingDetails.activeRow?.get("order_id");
        if (orderId) {
            this.layoutFreightItems.sourceBillingFreightGroup.search({ order_id: orderId }).then((fgi) => {
                const fgData = fgi.modelRows;
                this.layoutFreightItems.setFreightGroupItems(fgData);

                const fgpUid = fgData[0]?.data["fgp_uid"];
                if (fgpUid) {
                    this.sourceBrltlOrderHdrXFgp.search({ fgp_uid: fgpUid });
                }
            });
        }
        this.layoutEdiBillingRates.layoutMovementRates.visible = false
        this.layoutEdiBillingRates.panelOrderCarrierPay.visible = false;
        this.setupDefMoveType();

        // Set up tooltips for EDI fields when data is loaded
        const ediRow = this.sourceEdiBillingDetails.activeRow;
        if (ediRow) {
            EdiBillingUtility.makeTooltipContent(this.txtCorrectionIndicator, ediRow);
            EdiBillingUtility.makeTooltipContent(this.txtServiceCode, ediRow);
            setBatchCodeTooltip(this.txtBatch, ediRow, "I", Alignment.TOP);
        }

        generateTransmissionLogQuickInfo(this.textboxTransmitted, this.mainDataSource?.activeRow, false);

        this.setPartnerNameQuickInfo();

        if (this.mainDataSource?.activeRow?.get("status") === REJECTED) {
            this.textboxStatus.tooltip = `Rejected Reason: ${this.mainDataSource?.activeRow?.get("error_text") ?? null}`;
        } else {
            this.textboxStatus.tooltip = null;
        }

        this.setFieldsByDirection();
    }
    /**
     * Sets component visibility and captions based on the direction field
     */
    private setFieldsByDirection(): void {
        const direction = this.mainDataSource?.activeRow?.get("direction");
        const isInbound = direction === "I";

        // Set visibility to false for inbound direction, true for outbound
        this.textReadyToSend.visible = !isInbound;
        this.checkRebill.visible = !isInbound;
        this.txtError.visible = !isInbound;
        this.checkBalanceDue.visible = !isInbound;
        this.textboxXferred2billingDt.visible = !isInbound;
        this.textboxBillDate.visible = !isInbound;
        this.checkboxReadyToProcess.visible = !isInbound;
        this.checkboxPrinted.visible = !isInbound;
        this.textboxBillingUserId.visible = !isInbound;
        this.dfXref.visible = !isInbound;
        if (this.dfXref.visible) this.setupXref(this.mainDataSource?.activeRow?.get("customer_ediloc"), this.dfXref);
        this.textboxTransmitted.visible = !isInbound;
        this.panelInvoiceHistoryTab.visible = !isInbound;
        this.textboxDriverId.visible = !isInbound;
        this.textboxTractorId.visible = !isInbound;
        this.textboxTrailerId.visible = !isInbound;
        this.textboxTrailer2Id.visible = !isInbound;

        // Remove the tab from the container and adjust borders for inbound records
        if (isInbound) {
            this.panelOrderTabs.remove(this.panelInvoiceHistoryTab, false);
            // Remove border from Movement tab since it's now the last tab
            if (this.panelMovementTab) {
                this.panelMovementTab.borderRightWidth = 0;
            }
            // Make the remaining tabs expand to fill the space
            if (this.panelOrderTab) {
                this.panelOrderTab.fillRow = true;
            }
            if (this.panelMovementTab) {
                this.panelMovementTab.fillRow = true;
            }
        } else {
            // For outbound records, ensure the tab is added back to the container
            if (!this.panelOrderTabs.contains(this.panelInvoiceHistoryTab)) {
                this.panelOrderTabs.add(this.panelInvoiceHistoryTab);
            }
            // Restore border on Movement tab for outbound records
            if (this.panelMovementTab) {
                this.panelMovementTab.borderRightWidth = 1;
            }
            // Reset fillRow properties for normal 3-tab layout
            if (this.panelOrderTab) {
                this.panelOrderTab.fillRow = false;
            }
            if (this.panelMovementTab) {
                this.panelMovementTab.fillRow = false;
            }
        }

        // Hide round trip checkboxes for inbound direction
        if (this.checkboxRoundTrip2) this.checkboxRoundTrip2.visible = !isInbound;
        if (this.checkboxRoundTrip) this.checkboxRoundTrip.visible = !isInbound;

        //true for inbound
        this.textboxStatus.enabled = isInbound;
        this.textboxShipMatchType.enabled = isInbound;
        this.textboxShipMatchDescr.enabled = isInbound;
        this.textboxShipMatchType.visible = isInbound;
        this.textboxShipMatchDescr.visible = isInbound;

        // Change caption based on direction
        this.txtBatch.caption = isInbound ? "ISA Control #" : "Batch";
        this.stopsTable.visible = !isInbound;
        this.ediStopsTable.visible = isInbound;
    }
    viewOrder() {
        const orderId = this.sourceEdiBillingDetails.activeRow?.get("order_id");
        if (orderId != null)
            OrderTabView.navigateTo({ key: orderId, newTab: true })
    }

    private getRowData() {
        const row = {
            billing_id: this.sourceEdiBillingDetails.data[0].get("id"),
            order_id: this.sourceEdiBillingDetails.data[0].get("order_id"),
            movement_id: this.sourceOrders.data[0].get("curr_movement_id")
        };
        const data = [row];
        return data;
    }

    async close() {
        const beforeCloseEvent = new CrudDecoratorCloseEvent(this.dataHeader, DataSourceMode.UPDATE, DataHeaderCloseAction.CANCEL, true);
        this.dataHeader.fireBeforeCloseListener(beforeCloseEvent);
        if (beforeCloseEvent.defaultPrevented === true) {
            log.debug(this, "Close of DataHeader cancelled: %o", this);
            return false;
        }
        if (this.dataHeader.navigateOnClose != null)
            await this.dataHeader.navigateOnClose(DataHeaderCloseAction.CANCEL);
        else {
            const destinationPath = this.dataHeader.afterClosePath ?? "";
            await Navigation.navigateTo(destinationPath);
        }
        const afterCloseEvent = new CrudDecoratorCloseEvent(this.dataHeader, DataSourceMode.UPDATE, DataHeaderCloseAction.CANCEL, false);
        this.dataHeader.fireAfterCloseListener(afterCloseEvent);
    }

    handleApiResponse(response: any, successMessage: string, errorMessage?: string) {
        if (response) {
            setTimeout(() => Toast.showSuccessToast(successMessage), 500);
        } else if (errorMessage) {
            Snackbar.showWarningSnackbar(errorMessage);
        }
    }

    checkboxReadyToProcessOnClick(event: ClickEvent) {
        if (this.checkboxReadyToProcess.checked) {
            this.activeRow.set("billing_user_id", LMEUserSettings.getUserId());
            this.activeRow.setValueAndLookupModelData("billing_user_id",
                LMEUserSettings.getUserId(),
                LMEUserSettings.getUserLookupModelData());
            this.textboxBillingUserId.text = LMEUserSettings.getUserName();
        } else {
            this.activeRow.set("billing_user_id", null);
            this.activeRow.removeLookupModelData("billing_user_id");
            this.textboxBillingUserId.text = "";
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of txtServiceCode. */
    txtServiceCodeBeforeLookupModelSearch(event) {
        EdiBillingUtility.setupServiceCodeLookupFilter(event, this.sourceEdiBillingDetails.activeRow);
    }

    /** This is an event handler for the afterExecution event of sourceEdiMapError. */
    sourceEdiMapErrorAfterExecution(event: DataSourceExecutionEvent) {
        EdiBillingUtility.handleEdiMapErrorAfterExecution(event, this.tabErrors);
    }
    async corrIndBeforeLookupModelSearch(event) {
        EdiBillingUtility.setupCorrectionIndicatorLookupFilter(event, this.sourceEdiBillingDetails.activeRow);
    }

    /** This is an event handler for the onClick event of dataheaderOrders-viewCompareHistory. */
    showCompareHistoryOnClick(event: ClickEvent) {

    }

    async afterOrdersExecution(event?: DataSourceExecutionEvent) {
        await this.setCommodityQuickInfo();
        if (event?.getAction() === DataSourceAction.UPDATE) {
            this.activeRow.clearBeforePostListeners();
            this.sourceEdiBillingDetails.addAfterSearchPlusChildrenListener(async () => {
                if (this.shouldOpenPaySlideout) {
                    history.replaceState({ additionalProps: { modifyPay: true } }, "", window.location.search);
                    this.panelMovementTab.clicked();
                    this.shouldOpenPaySlideout = false;
                }

                // These fields are really part of the movement table, but they are used in the order's API data to update the movement.
                // They are no longer needed on the active row after posting, so set them to undefined to prevent reenabling the save button
                if (!this.sourceEdiBillingDetails.activeRow?.isNull("movement.override_targetpay")) {
                    this.sourceEdiBillingDetails.activeRow.set("movement.override_targetpay", undefined);
                }
                if (!this.sourceEdiBillingDetails.activeRow?.isNull("movement.override_max_pay")) {
                    this.sourceEdiBillingDetails.activeRow.set("movement.override_max_pay", undefined);
                }
            });
        } else if (event?.getAction() === DataSourceAction.SEARCH) {
            this.addLayoutDataLoadListener(async () => {
                const overviewLayout = this.layoutOverview as unknown as EdiBillingOverview;
                (overviewLayout["layoutStopProgress"] as StopProgress).setStopsTable(this.mainDataSource.activeRow.get("direction") === OUTBOUND ? this.stopsTable : this.ediStopsTable);
                const orderId = this.sourceOrders.activeRow?.get("id");
                if (orderId) {
                    (this.layoutImageTable as ImageTable).populateForRow("O", orderId);
                }
                this.switchRecurringOrder.checked = (null != this.sourceOrders.activeRow?.get("recurring_order_id"));
                this.layoutEdiBillingRates.carrierPayLayout.configureForOrderEntry(this.sourceOrders, true);
                const isHazmat = await this.getIsHazmatCommodity();
                this.showHazmatWarning(isHazmat);
                if (this.isBrokerageLtlCompany)
                    this.getRatingDetails(this.sourceOrders.activeRow?.get("id"));
                this.setupPreloadedTrailerLookup();
            });
        }
    }

    private setupEquipmentTypeIdTooltipOverrides(textbox: Textbox) {
        textbox.quickInfoOverrideSearchMethod = async (layout: Layout, keyValue: any) => {
            const equipLayout = layout as EquipmentTypeQuickInfo;
            equipLayout.textboxPartnerValue.visible = true;
            await equipLayout.mainDataSource?.search({ search: keyValue });
            const modelRow: ModelRow = ArrayUtil.getFirstElement(equipLayout.mainDataSource?.data);
            modelRow?.set("partner_value", this.sourceEdiBillingDetails.activeRow?.get("edi_equip_type_id"));
        };
    }

    async setCommodityQuickInfo() {
        const partnerValue = this.sourceEdiBillingDetails.activeRow?.get("edi_commodity_id");
        this.textboxCommodityId.quickInfoOverrideSearchMethod = async (layout: Layout, keyValue: any) => {
            await layout.mainDataSource.search({ id: keyValue });
            const row: ModelRow = ArrayUtil.getFirstElement(layout.mainDataSource?.data);
            if (row != null) {
                row.set("partner_value_visible", true);
                if (partnerValue !== undefined) {
                    row.set("partner_value", partnerValue);
                }
            }
        };
    }

    beforeBillingExecution(event: DataSourceExecutionEvent) {
        if ((event.getAction() === DataSourceAction.UPDATE)) {
            this.addBeforePostListeners();
        }
    }


    addBeforePostListeners() {
        if (this.mainDataSource.activeRow != null && this.mainDataSource.activeRow.get("direction") === "O") {
            const row = this.mainDataSource.activeRow;
            if (this.beforePostListener != null) {
                row.clearBeforePostListeners();
                this.beforePostListener = null;
            }
            this.beforePostListener = async postEvent => {
                return new Promise(async (resolve) => {
                    const batch = row.get("batch");
                    if (batch == null) {
                        resolve(true);
                        return;
                    }

                    let dialogMessage = "Changes have been made to the DataFusion invoice record, but not the order.";
                    if (batch !== 0) {
                        dialogMessage += " Do you want to retransmit (reset the batch to 0) and save these changes?";
                    } else {
                        dialogMessage += " Do you want to save these changes?";
                    }

                    const result = await CommonDialogs.showYesNo(
                        dialogMessage,
                        "Retransmit Invoice"
                    );

                    if (result === true) {
                        // If batch is not 0, reset it to 0 before saving
                        if (batch !== 0) {
                            row.set("batch", 0);
                        }
                        resolve(true);
                    } else {
                        // Cancel the save operation for No or dialog canceled
                        postEvent.preventDefault("Changes not saved.");
                        resolve(false);
                    }
                });
            }
            row.addBeforePostListener(this.beforePostListener);
        }
    }

    setupXref(locId: any, labelXref: Label) {
        if (locId == undefined || locId == "") {
            this.setLabelXrefProperties(labelXref, true, ImageName.cross_ref_missing, "default.darkest", "Customer is missing a cross reference to DataFusion customer.");
        } else {
            if (labelXref != undefined) {
                this.setLabelXrefProperties(labelXref, true, ImageName.cross_ref_confirmed, "default.darkest", "Customer cross reference successfully applied");
            }
        }
    }

    private setLabelXrefProperties(labelXref: Label, visible: boolean, imageName: ImageName, imageColor: string, tooltip: string) {
        labelXref.visible = visible;
        labelXref.imageName = imageName;
        labelXref.imageColor = imageColor;
        labelXref.tooltip = tooltip;
    }

    dfXrefOnClick() {
        InvoiceCustomerCrossReference.showSlideout({
            customerEdiloc: this.sourceEdiBillingDetails.activeRow?.get("customer_ediloc"),
            version: this.sourceEdiBillingDetails.activeRow?.get("version"),
            partnerId: this.sourceEdiBillingDetails.activeRow?.get("partner_id"),
            customerId: this.sourceEdiBillingDetails.activeRow?.get("customer_id"),
            ediBillingId: this.sourceEdiBillingDetails.activeRow?.get("id"),
            doAfterSlideOut: async () => {
                // Perform any actions needed after the slide-out is closed
                await this.mainDataSource.search(this.mainDataSource.lastSearch);
            }
        });
    }

    async afterDropdownSelectionStatus(event: DropdownSelectionEvent) {
        if (event.userInitiatedChange) {
            const newStatus = event.selection?.value;
            const oldStatus = event.deselection?.value;
            if (newStatus === REJECTED && oldStatus !== REJECTED) {
                const direction = this.mainDataSource.activeRow?.get("direction");
                if (direction !== INBOUND) return;
                const existingReason = this.mainDataSource.activeRow?.get("error_text") ?? "";
                const reason = await CommonDialogs.showInputDialog(
                    "Enter a reason for the rejection, or modify the existing one:",
                    "Reason for Invoice Rejection",
                    { text: existingReason }
                );
                if (reason != null) {
                    this.mainDataSource.activeRow?.set("error_text", reason);
                    this.textboxStatus.tooltip = `Rejected Reason: ${reason}`;
                } else {
                    this.mainDataSource.activeRow?.set("status", oldStatus);
                }
            } else if (newStatus !== REJECTED) {
                this.mainDataSource.activeRow?.set("error_text", null);
                this.textboxStatus.tooltip = null;
            }
        }
    }
}
